import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Cable,
  MessageSquareText,
  Image,
  Video,
  Mic,
  FileText,
  Link,
  MapPin,
  List,
  ToggleLeft,
  Users,
  RefreshCcw,
  Trash2,
  Save,
  Plus,
  Phone,
  Home,
  GalleryHorizontal,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Play,
  Pencil,
  Calendar,
  ChevronDown,
  ChevronRight,
  X,
  ChevronLeft,
} from 'lucide-react';
import { botsApi } from '../../../api/bots.api';
import { faqApi } from '../../../api/faq.api';
import { uploadApi, validateMediaFile, FILE_ACCEPT_STRINGS } from '../../../api/upload.api';
import useConfirm from '../../../hooks/useConfirm';

const BLOCK_WIDTH = 260;
const BLOCK_HEIGHT = 120;

// Block type configurations with colors and gradients
const blockTypeConfig = {
  text: {
    gradient: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
    color: '#3b82f6',
    lightBg: 'rgba(59, 130, 246, 0.1)',
    icon: MessageSquareText,
  },
  appointment: {
    gradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
    color: '#06b6d4',
    lightBg: 'rgba(6, 182, 212, 0.1)',
    icon: Calendar,
  },
  image: {
    gradient: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
    color: '#8b5cf6',
    lightBg: 'rgba(139, 92, 246, 0.1)',
    icon: Image,
  },
  video: {
    gradient: 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)',
    color: '#ec4899',
    lightBg: 'rgba(236, 72, 153, 0.1)',
    icon: Video,
  },
  audio: {
    gradient: 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)',
    color: '#14b8a6',
    lightBg: 'rgba(20, 184, 166, 0.1)',
    icon: Mic,
  },
  document: {
    gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
    color: '#f59e0b',
    lightBg: 'rgba(245, 158, 11, 0.1)',
    icon: FileText,
  },
  contact: {
    gradient: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    color: '#22c55e',
    lightBg: 'rgba(34, 197, 94, 0.1)',
    icon: Phone,
  },
  address: {
    gradient: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
    color: '#22c55e',
    lightBg: 'rgba(34, 197, 94, 0.1)',
    icon: Home,
  },
  location: {
    gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: '#10b981',
    lightBg: 'rgba(16, 185, 129, 0.1)',
    icon: MapPin,
  },
  location_request: {
    gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: '#10b981',
    lightBg: 'rgba(16, 185, 129, 0.1)',
    icon: MapPin,
  },
  faq_categories: {
    gradient: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
    color: '#f97316',
    lightBg: 'rgba(249, 115, 22, 0.1)',
    icon: MessageSquareText,
  },
  carousel: {
    gradient: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
    color: '#0ea5e9',
    lightBg: 'rgba(14, 165, 233, 0.1)',
    icon: GalleryHorizontal,
  },
  interactive_button: {
    gradient: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
    color: '#f97316',
    lightBg: 'rgba(249, 115, 22, 0.1)',
    icon: ToggleLeft,
  },
  interactive_list: {
    gradient: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
    color: '#06b6d4',
    lightBg: 'rgba(6, 182, 212, 0.1)',
    icon: List,
  },
  cta_url: {
    gradient: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
    color: '#0ea5e9',
    lightBg: 'rgba(14, 165, 233, 0.1)',
    icon: Link,
  },
  live_chat: {
    gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
    color: '#ef4444',
    lightBg: 'rgba(239, 68, 68, 0.1)',
    icon: Users,
  },
  template: {
    gradient: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
    color: '#6366f1',
    lightBg: 'rgba(99, 102, 241, 0.1)',
    icon: Cable,
  },
};

const messageBlocks = [
  { type: 'text', label: 'Text', icon: MessageSquareText },
  { type: 'appointment', label: 'Appointment', icon: Calendar },
  { type: 'image', label: 'Image', icon: Image },
  { type: 'video', label: 'Video', icon: Video },
  { type: 'audio', label: 'Audio', icon: Mic },
  { type: 'document', label: 'Document', icon: FileText },
  { type: 'contact', label: 'Contacts', icon: Phone },
  { type: 'address', label: 'Address Request', icon: Home },
  { type: 'location', label: 'Location', icon: MapPin },
  { type: 'location_request', label: 'Location Request', icon: MapPin },
];

const interactiveBlocks = [
  { type: 'interactive_button', label: 'Buttons', icon: ToggleLeft },
  { type: 'interactive_list', label: 'List', icon: List },
  { type: 'cta_url', label: 'CTA URL', icon: Link },
  { type: 'carousel', label: 'Carousel', icon: GalleryHorizontal },
  { type: 'faq_categories', label: 'FAQ Categories', icon: MessageSquareText },
];

const specialBlocks = [
  { type: 'live_chat', label: 'Live Chat', icon: Users },
];

const parseFlowBlocks = (payload) => {
  const rows = payload?.blocks || [];
  return rows.map((block) => {
    const message = block.message ? { ...block.message } : null;
    if (message?.metadata?.buttons && !message.buttons) {
      message.buttons = message.metadata.buttons;
    }
    if (message?.metadata?.rows && !message.rows) {
      message.rows = message.metadata.rows;
    }
    return {
      ...block,
      position: block.position || { x: 120, y: 120 },
      message,
      connections: Array.isArray(block.connections) ? block.connections : [],
    };
  });
};

const normalizeFaqCategory = (category) => ({
  id: category?.id ?? category?.cid ?? category?.category_id,
  name: category?.name ?? category?.c_name ?? category?.category_name ?? '',
});

const normalizeFaqItem = (faq) => ({
  faq_id: faq?.faq_id ?? faq?.id,
  faq_question: faq?.faq_question ?? faq?.question ?? '',
  cid: faq?.cid ?? faq?.category_id ?? faq?.categoryId ?? faq?.category?.id ?? '',
});

const normalizeMessages = (response) => {
  const data = response?.data?.data || response?.data || [];
  return Array.isArray(data) ? data : [];
};

const BotBuilderPage = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { confirm } = useConfirm();
  const palette = theme.palette;
  const isDark = palette.mode === 'dark';
  const surface = palette.background.paper;
  const surfaceAlt = palette.background.default;
  const borderTone = palette.divider;
  const textPrimary = palette.text.primary;
  const textSecondary = palette.text.secondary;
  const panelTint = alpha(textPrimary, isDark ? 0.08 : 0.05);
  const cardBorder = alpha(textPrimary, isDark ? 0.18 : 0.14);
  const cardShadow = isDark
    ? '0 8px 32px rgba(0,0,0,0.3)'
    : `0 8px 24px ${alpha(palette.common.black, 0.12)}`;
  const cardShadowActive = isDark
    ? '0 20px 40px rgba(0,0,0,0.35)'
    : `0 18px 36px ${alpha(palette.common.black, 0.18)}`;
  const headerBg = isDark
    ? 'linear-gradient(90deg, #0f172a 0%, #1e293b 100%)'
    : `linear-gradient(90deg, ${alpha(palette.primary.main, 0.12)} 0%, ${alpha(palette.primary.main, 0.05)} 100%)`;
  const canvasDot = alpha(textPrimary, isDark ? 0.16 : 0.12);
  const [searchParams] = useSearchParams();
  const [bots, setBots] = useState([]);
  const [currentBotId, setCurrentBotId] = useState('');
  const [blocks, setBlocks] = useState([]);
  const [selectedBlockId, setSelectedBlockId] = useState(null);
  const [connecting, setConnecting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [flowLoading, setFlowLoading] = useState(false);
  const [error, setError] = useState('');
  const [editor, setEditor] = useState({
    name: '',
    isStart: false,
    body: '',
    listHeader: '',
    listFooter: '',
    listButtonText: '',
    buttonHeader: '',
    buttonFooter: '',
    buttonHeaderType: 'text',
    buttonHeaderLink: '',
    ctaHeader: '',
    ctaFooter: '',
    ctaButtonText: '',
    ctaHeaderType: 'text',
    ctaHeaderLink: '',
    url: '',
    contacts: [],
    addressCountry: '',
    carouselCards: [],
    faqHeader: '',
    faqFooter: '',
    faqButtonText: '',
    faqCategoryIds: [],
    faqIds: [],
    items: [],
    location: { latitude: '', longitude: '', name: '', address: '' },
    template: { name: '', language: 'en' },
    trigger: 'auto',
    connectTo: '',
  });
  const [isLibraryOpen, setIsLibraryOpen] = useState(true);
  const [isInspectorOpen, setIsInspectorOpen] = useState(true);
  const [isMessagesOpen, setIsMessagesOpen] = useState(true);
  const [isTipsOpen, setIsTipsOpen] = useState(true);
  const [faqCategories, setFaqCategories] = useState([]);
  const [faqItems, setFaqItems] = useState([]);
  const [botMediaUploading, setBotMediaUploading] = useState(false);
  const [carouselUploading, setCarouselUploading] = useState({});
  const [ctaHeaderUploading, setCtaHeaderUploading] = useState(false);
  const [buttonHeaderUploading, setButtonHeaderUploading] = useState(false);

  // Canvas zoom and pan state
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  const canvasRef = useRef(null);
  const draggingRef = useRef(null);
  const dragFrameRef = useRef(null);
  const botMediaInputRef = useRef(null);
  const carouselInputRefs = useRef([]);
  const ctaHeaderInputRef = useRef(null);
  const buttonHeaderInputRef = useRef(null);

  const selectedBlock = useMemo(
    () => blocks.find((block) => block.id === selectedBlockId) || null,
    [blocks, selectedBlockId]
  );

  const loadBots = useCallback(async () => {
    try {
      const response = await botsApi.getAll();
      const rows = response?.data?.data || response?.data || [];
      const normalized = Array.isArray(rows) ? rows : [];
      setBots(normalized);
      if (!currentBotId && normalized.length) {
        const qsBotId = searchParams.get('botId');
        const firstId = qsBotId || normalized[0]?.bid;
        if (firstId) {
          setCurrentBotId(String(firstId));
        }
      }
    } catch (err) {
      setError('Failed to load bots. Please refresh.');
    }
  }, [currentBotId, searchParams]);

  const loadFlow = useCallback(async (botId) => {
    if (!botId) return;
    setFlowLoading(true);
    setError('');
    try {
      const response = await botsApi.getWaFlow(botId);
      const payload = response?.data?.data || response?.data || {};
      setBlocks(parseFlowBlocks(payload));
      setSelectedBlockId(null);
    } catch (err) {
      setError('Failed to load flow. Please try again.');
      setBlocks([]);
    } finally {
      setFlowLoading(false);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    loadBots().finally(() => setLoading(false));
  }, [loadBots]);

  useEffect(() => {
    if (currentBotId) {
      loadFlow(currentBotId);
    }
  }, [currentBotId, loadFlow]);

  const filteredFaqItems = useMemo(() => {
    const selectedCategoryIds = Array.isArray(editor.faqCategoryIds)
      ? editor.faqCategoryIds.map((id) => Number(id)).filter((id) => Number.isFinite(id))
      : [];
    if (!selectedCategoryIds.length) return faqItems;
    const categorySet = new Set(selectedCategoryIds);
    return faqItems.filter((faq) => categorySet.has(Number(faq.cid)));
  }, [editor.faqCategoryIds, faqItems]);

  useEffect(() => {
    if (!selectedBlock) {
      setEditor({
        name: '',
        isStart: false,
        body: '',
        listHeader: '',
        listFooter: '',
        listButtonText: '',
        buttonHeader: '',
        buttonFooter: '',
        buttonHeaderType: 'text',
        buttonHeaderLink: '',
        ctaHeader: '',
        ctaFooter: '',
        ctaButtonText: '',
        ctaHeaderType: 'text',
        ctaHeaderLink: '',
        url: '',
        contacts: [],
        addressCountry: '',
        carouselCards: [],
        faqHeader: '',
        faqFooter: '',
        faqButtonText: '',
        faqCategoryIds: [],
        faqIds: [],
        items: [],
        location: { latitude: '', longitude: '', name: '', address: '' },
        template: { name: '', language: 'en' },
        trigger: 'auto',
        connectTo: '',
      });
      return;
    }

    const metadata = selectedBlock.message?.metadata || {};
    const templateMeta = metadata.template || metadata;

    setEditor({
      name: selectedBlock.name || '',
      isStart: selectedBlock.is_start || false,
      body: selectedBlock.message?.body || '',
      listHeader: metadata.header || '',
      listFooter: metadata.footer || '',
      listButtonText: metadata.button || '',
      buttonHeader: typeof metadata.header === 'string' ? metadata.header : '',
      buttonFooter: metadata.footer || '',
      buttonHeaderType:
        typeof metadata.header === 'object' && metadata.header?.type
          ? metadata.header.type
          : metadata.header
          ? 'text'
          : 'text',
      buttonHeaderLink:
        typeof metadata.header === 'object' && metadata.header?.link ? metadata.header.link : '',
      ctaHeader: typeof metadata.header === 'string' ? metadata.header : '',
      ctaFooter: metadata.footer || '',
      ctaButtonText: metadata.buttonText || metadata.button_text || metadata.display_text || '',
      ctaHeaderType:
        typeof metadata.header === 'object' && metadata.header?.type
          ? metadata.header.type
          : metadata.header
          ? 'text'
          : 'text',
      ctaHeaderLink:
        typeof metadata.header === 'object' && metadata.header?.link ? metadata.header.link : '',
      url: selectedBlock.message?.url || '',
      contacts: Array.isArray(metadata?.contacts) ? metadata.contacts : [],
      addressCountry: metadata?.country || '',
      carouselCards: Array.isArray(metadata?.cards) ? metadata.cards : [],
      faqHeader: metadata?.header || '',
      faqFooter: metadata?.footer || '',
      faqButtonText: metadata?.button || '',
      faqCategoryIds: Array.isArray(metadata?.categoryIds) ? metadata.categoryIds : [],
      faqIds: Array.isArray(metadata?.faqIds) ? metadata.faqIds : [],
      items:
        selectedBlock.type === 'interactive_button'
          ? selectedBlock.message?.buttons || selectedBlock.message?.metadata?.buttons || []
          : selectedBlock.type === 'interactive_list'
          ? selectedBlock.message?.rows || selectedBlock.message?.metadata?.rows || []
          : [],
      location: {
        latitude:
          metadata.latitude !== undefined && metadata.latitude !== null
            ? String(metadata.latitude)
            : '',
        longitude:
          metadata.longitude !== undefined && metadata.longitude !== null
            ? String(metadata.longitude)
            : '',
        name: metadata.name || '',
        address: metadata.address || '',
      },
      template: {
        name: templateMeta?.name || '',
        language: templateMeta?.language || 'en',
      },
      trigger: 'auto',
      connectTo: '',
    });
  }, [selectedBlock]);

  const handleSelectBlock = (blockId) => {
    setSelectedBlockId(blockId);
    setConnecting(null);
    setIsInspectorOpen(false);
  };

  const handleOpenBlockEditor = (blockId) => {
    setSelectedBlockId(blockId);
    setConnecting(null);
    setIsInspectorOpen(true);
  };

  const handleCreateBlock = async (type) => {
    if (!currentBotId) return;
    setError('');
    const position = {
      x: 120 + blocks.length * 30,
      y: 120 + blocks.length * 24,
    };

    try {
      const response = await botsApi.createWaBlock(currentBotId, {
        name: `${type.replace('_', ' ')} block`,
        type,
        is_start: blocks.length === 0,
        position,
      });
      const created = response?.data?.data || response?.data;
      const normalized = {
        ...created,
        position: created?.position || position,
        connections: [],
        message: created?.message || null,
      };
      setBlocks((prev) => [...prev, normalized]);
      setSelectedBlockId(created?.id);
    } catch (err) {
      setError('Unable to create block. Please try again.');
    }
  };

  const handleDeleteBlock = async () => {
    if (!selectedBlock) return;
    const isConfirmed = await confirm({
      title: 'Delete Block',
      message: `Delete "${selectedBlock.name}"?`,
      confirmText: 'Delete',
      confirmColor: 'error',
      severity: 'error',
    });
    if (!isConfirmed) return;

    try {
      await botsApi.deleteWaBlock(selectedBlock.id);
      setBlocks((prev) =>
        prev
          .filter((block) => block.id !== selectedBlock.id)
          .map((block) => ({
            ...block,
            connections: (block.connections || []).filter(
              (conn) => conn.to_block_id !== selectedBlock.id
            ),
          }))
      );
      setSelectedBlockId(null);
    } catch (err) {
      setError('Failed to delete block.');
    }
  };

  const ensureMessage = async (block, payload = {}) => {
    const messages = normalizeMessages(await botsApi.getWaMessages(block.id));
    if (messages.length) return messages[0];
    const response = await botsApi.createWaMessage(block.id, {
      type: block.type,
      ...payload,
    });
    return response?.data?.data || response?.data;
  };

  const handleSaveBlock = async () => {
    if (!selectedBlock) return;
    setError('');

    if (['text', 'appointment'].includes(selectedBlock.type) && !editor.body.trim()) {
      setError('Text blocks need a message body.');
      return;
    }

    if (
      ['image', 'video', 'audio', 'document'].includes(selectedBlock.type) &&
      !editor.url.trim()
    ) {
      setError('Media blocks need a valid URL.');
      return;
    }

    if (
      ['interactive_button', 'interactive_list'].includes(selectedBlock.type) &&
      editor.items.length === 0
    ) {
      setError('Add at least one option for interactive blocks.');
      return;
    }

    if (selectedBlock.type === 'interactive_button') {
      if (!editor.body.trim()) {
        setError('Button blocks need a body message.');
        return;
      }
      const invalid = editor.items.some((item) => !item.id || !item.title);
      if (invalid) {
        setError('Each button needs an ID and title.');
        return;
      }
      if (editor.buttonHeaderType !== 'text' && !editor.buttonHeaderLink.trim()) {
        setError('Button headers need a media URL.');
        return;
      }
      if (editor.buttonHeaderType === 'text' && editor.buttonHeader.length > 60) {
        setError('Button header text must be 60 characters or fewer.');
        return;
      }
    }

    if (selectedBlock.type === 'interactive_list') {
      if (!editor.body.trim()) {
        setError('List blocks need a body message.');
        return;
      }
      if (!editor.listHeader.trim()) {
        setError('List blocks need a header text.');
        return;
      }
      if (!editor.listFooter.trim()) {
        setError('List blocks need a footer text.');
        return;
      }
      if (!editor.listButtonText.trim()) {
        setError('List blocks need a button label.');
        return;
      }
      const invalid = editor.items.some((item) => !item.id || !item.title);
      if (invalid) {
        setError('Each list item needs an ID and title.');
        return;
      }
    }

      if (selectedBlock.type === 'cta_url') {
        if (!editor.body.trim()) {
          setError('CTA URL blocks need a body message.');
          return;
        }
        if (!editor.ctaButtonText.trim()) {
          setError('CTA URL blocks need a button label.');
          return;
        }
        if (editor.ctaButtonText.trim().length > 20) {
          setError('CTA URL button label must be 20 characters or fewer.');
          return;
        }
        if (!editor.url.trim()) {
          setError('CTA URL blocks need a valid URL.');
          return;
        }
        if (editor.ctaHeaderType !== 'text' && !editor.ctaHeaderLink.trim()) {
          setError('CTA URL headers need a media URL.');
          return;
        }
        if (editor.ctaHeaderType === 'text' && editor.ctaHeader.length > 60) {
          setError('CTA URL header text must be 60 characters or fewer.');
          return;
        }
      }

    if (selectedBlock.type === 'contact') {
      if (!Array.isArray(editor.contacts) || editor.contacts.length === 0) {
        setError('Contacts blocks need at least one contact.');
        return;
      }
      const hasMissingName = editor.contacts.some(
        (contact) => !contact?.name?.formatted_name || !String(contact.name.formatted_name).trim()
      );
      if (hasMissingName) {
        setError('Each contact needs a formatted name.');
        return;
      }
    }

    if (selectedBlock.type === 'location_request') {
      if (!editor.body.trim()) {
        setError('Location request blocks need a body message.');
        return;
      }
    }

    if (selectedBlock.type === 'carousel') {
      if (!editor.body.trim()) {
        setError('Carousel blocks need a body message.');
        return;
      }
      const cards = editor.carouselCards || [];
      if (cards.length < 2 || cards.length > 10) {
        setError('Carousel blocks need between 2 and 10 cards.');
        return;
      }
      const invalidCard = cards.some(
        (card) =>
          !card?.header?.type ||
          !card?.header?.link ||
          !card?.button?.text ||
          !card?.button?.url
      );
      if (invalidCard) {
        setError('Each card needs a header type/link and a button label/URL.');
        return;
      }
    }

    if (selectedBlock.type === 'faq_categories') {
      if (!editor.body.trim()) {
        setError('FAQ blocks need a body message.');
        return;
      }
    }

    if (selectedBlock.type === 'address') {
      if (!editor.body.trim()) {
        setError('Address request blocks need a body message.');
        return;
      }
      if (!editor.addressCountry.trim()) {
        setError('Address request blocks need a country code.');
        return;
      }
    }

    if (selectedBlock.type === 'location') {
      const lat = Number(editor.location.latitude);
      const lng = Number(editor.location.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        setError('Location requires valid latitude and longitude.');
        return;
      }
    }

    if (selectedBlock.type === 'template' && !editor.template.name.trim()) {
      setError('Template name is required.');
      return;
    }

    try {
      const updated = await botsApi.updateWaBlock(selectedBlock.id, {
        name: editor.name.trim() || selectedBlock.name,
        is_start: editor.isStart,
      });
      const updatedBlock = updated?.data?.data || updated?.data;
      setBlocks((prev) =>
        prev.map((block) => {
          if (block.id !== selectedBlock.id) {
            return editor.isStart ? { ...block, is_start: false } : block;
          }
          return { ...block, ...updatedBlock };
        })
      );

      const messagePayload = {};
      if (
        [
          'text',
          'appointment',
          'template',
          'live_chat',
          'interactive_button',
          'interactive_list',
          'cta_url',
          'location_request',
          'address',
          'carousel',
          'faq_categories',
        ].includes(selectedBlock.type)
      ) {
        messagePayload.body = editor.body;
      }

      if (['image', 'video', 'audio', 'document', 'cta_url'].includes(selectedBlock.type)) {
        messagePayload.url = editor.url;
      }

      if (selectedBlock.type === 'interactive_button') {
        messagePayload.metadata = {
          buttons: editor.items,
          header:
            editor.buttonHeaderType && editor.buttonHeaderType !== 'text'
              ? {
                  type: editor.buttonHeaderType,
                  link: editor.buttonHeaderLink.trim() || undefined,
                }
              : editor.buttonHeader.trim() || undefined,
          footer: editor.buttonFooter.trim() || undefined,
        };
      }

      if (selectedBlock.type === 'interactive_list') {
        messagePayload.metadata = {
          rows: editor.items.map((item) => ({ ...item, section: item.section || 'Options' })),
          header: editor.listHeader.trim(),
          footer: editor.listFooter.trim(),
          button: editor.listButtonText.trim(),
        };
      }

      if (selectedBlock.type === 'cta_url') {
        messagePayload.metadata = {
          buttonText: editor.ctaButtonText.trim(),
          header:
            editor.ctaHeaderType && editor.ctaHeaderType !== 'text'
              ? {
                  type: editor.ctaHeaderType,
                  link: editor.ctaHeaderLink.trim() || undefined,
                }
              : editor.ctaHeader.trim() || undefined,
          footer: editor.ctaFooter.trim() || undefined,
        };
      }

      if (selectedBlock.type === 'contact') {
        messagePayload.metadata = {
          contacts: editor.contacts || [],
        };
      }

      if (selectedBlock.type === 'address') {
        messagePayload.metadata = {
          country: editor.addressCountry.trim(),
        };
      }

      if (selectedBlock.type === 'carousel') {
        messagePayload.metadata = {
          cards: editor.carouselCards || [],
        };
      }

      if (selectedBlock.type === 'faq_categories') {
        messagePayload.metadata = {
          header: editor.faqHeader.trim() || undefined,
          footer: editor.faqFooter.trim() || undefined,
          button: editor.faqButtonText.trim() || undefined,
          categoryIds: editor.faqCategoryIds || [],
          faqIds: editor.faqIds || [],
        };
      }

      if (selectedBlock.type === 'location') {
        messagePayload.metadata = {
          latitude: Number(editor.location.latitude),
          longitude: Number(editor.location.longitude),
          name: editor.location.name || undefined,
          address: editor.location.address || undefined,
        };
      }

      if (selectedBlock.type === 'template') {
        messagePayload.metadata = {
          name: editor.template.name.trim(),
          language: editor.template.language || 'en',
        };
      }

      if (Object.keys(messagePayload).length > 0) {
        const message = await ensureMessage(selectedBlock, messagePayload);
        if (message?.id) {
          await botsApi.updateWaMessage(message.id, messagePayload);
        }
        const nextMessage = {
          ...(selectedBlock.message || {}),
          ...messagePayload,
        };
        if (selectedBlock.type === 'interactive_button') {
          nextMessage.buttons = editor.items;
        }
        if (selectedBlock.type === 'interactive_list') {
          nextMessage.rows = editor.items;
        }
        setBlocks((prev) =>
          prev.map((block) =>
            block.id === selectedBlock.id
              ? {
                  ...block,
                  message: nextMessage,
                }
              : block
          )
        );
      }
    } catch (err) {
      setError('Failed to update block. Please try again.');
    }
  };

  const handleStartConnection = (blockId, trigger) => {
    const source = blocks.find((block) => block.id === blockId);
    if (source?.type === 'live_chat' || source?.type === 'appointment') {
      setError('This block type cannot connect to another block.');
      return;
    }
    setConnecting({ blockId, trigger: trigger || null });
  };

  const handleTargetConnection = async (targetId) => {
    if (!connecting || !currentBotId) return;
    const { blockId, trigger } = connecting;
    if (blockId === targetId) {
      setConnecting(null);
      return;
    }

    try {
      const response = await botsApi.createWaConnection(currentBotId, {
        from_block_id: blockId,
        to_block_id: targetId,
        action_value: trigger || null,
      });
      const created = response?.data?.data || response?.data;
      setBlocks((prev) =>
        prev.map((block) => {
          if (block.id !== blockId) return block;
          const triggerKey = trigger || 'auto';
          const cleaned = (block.connections || []).filter((c) => c.trigger !== triggerKey);
          return {
            ...block,
            connections: [
              ...cleaned,
              {
                connection_id: created?.id,
                to_block_id: targetId,
                trigger: triggerKey,
              },
            ],
          };
        })
      );
    } catch (err) {
      setError('Failed to create connection.');
    } finally {
      setConnecting(null);
    }
  };

  const handleDeleteConnection = async (connectionId) => {
    if (!connectionId) return;
    try {
      await botsApi.deleteWaConnection(connectionId);
      setBlocks((prev) =>
        prev.map((block) => ({
          ...block,
          connections: (block.connections || []).filter(
            (conn) => conn.connection_id !== connectionId
          ),
        }))
      );
    } catch (err) {
      setError('Failed to delete connection.');
    }
  };

  const handleCleanupConnections = async () => {
    if (!currentBotId) return;
    try {
      await botsApi.cleanupWaConnections(currentBotId);
      await loadFlow(currentBotId);
    } catch (err) {
      setError('Failed to clean up connections.');
    }
  };

  const handleUpdatePosition = async (blockId, position) => {
    try {
      await botsApi.updateWaBlock(blockId, { position });
    } catch (err) {
      setError('Failed to save block position.');
    }
  };

  const handleMouseDown = (event, blockId) => {
    if (event.button !== 0) return;
    event.stopPropagation();
    event.preventDefault();
    const block = blocks.find((item) => item.id === blockId);
    if (!block) return;
    draggingRef.current = {
      blockId,
      startX: event.clientX,
      startY: event.clientY,
      originX: block.position?.x || 0,
      originY: block.position?.y || 0,
    };
  };

  useEffect(() => {
    const handleMouseMove = (event) => {
      if (!draggingRef.current) return;
      const { blockId, startX, startY, originX, originY } = draggingRef.current;
      const scale = zoom || 1;
      const nextX = Math.max(40, originX + (event.clientX - startX) / scale);
      const nextY = Math.max(40, originY + (event.clientY - startY) / scale);
      draggingRef.current.next = { blockId, x: nextX, y: nextY };
      if (dragFrameRef.current) return;
      dragFrameRef.current = window.requestAnimationFrame(() => {
        const pending = draggingRef.current?.next;
        dragFrameRef.current = null;
        if (!pending) return;
        setBlocks((prev) =>
          prev.map((block) =>
            block.id === pending.blockId
              ? { ...block, position: { x: pending.x, y: pending.y } }
              : block
          )
        );
      });
    };

    const handleMouseUp = () => {
      if (!draggingRef.current) return;
      const { blockId } = draggingRef.current;
      const block = blocks.find((item) => item.id === blockId);
      if (block?.position) {
        handleUpdatePosition(blockId, block.position);
      }
      draggingRef.current = null;
      if (dragFrameRef.current) {
        window.cancelAnimationFrame(dragFrameRef.current);
        dragFrameRef.current = null;
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [blocks, zoom]);

  // Zoom handlers
  const handleZoomIn = () => setZoom((z) => Math.min(z + 0.1, 2));
  const handleZoomOut = () => setZoom((z) => Math.max(z - 0.1, 0.3));
  const handleResetView = () => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  };

  // Canvas pan handlers
  const handleCanvasMouseDown = (e) => {
    if (e.target === canvasRef.current || e.target.tagName === 'svg') {
      setIsPanning(true);
      panStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        panX: pan.x,
        panY: pan.y,
      };
    }
  };

  useEffect(() => {
    const handlePanMove = (e) => {
      if (!isPanning) return;
      const dx = e.clientX - panStartRef.current.x;
      const dy = e.clientY - panStartRef.current.y;
      setPan({
        x: panStartRef.current.panX + dx,
        y: panStartRef.current.panY + dy,
      });
    };

    const handlePanEnd = () => setIsPanning(false);

    if (isPanning) {
      window.addEventListener('mousemove', handlePanMove);
      window.addEventListener('mouseup', handlePanEnd);
    }

    return () => {
      window.removeEventListener('mousemove', handlePanMove);
      window.removeEventListener('mouseup', handlePanEnd);
    };
  }, [isPanning]);

  // Handle mouse wheel for zoom
  const handleWheel = useCallback((e) => {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      const delta = e.deltaY > 0 ? -0.1 : 0.1;
      setZoom((z) => Math.min(Math.max(z + delta, 0.3), 2));
    }
  }, []);

  const connectionPaths = useMemo(() => {
    const lookup = new Map(blocks.map((block) => [block.id, block]));
    const paths = [];
    blocks.forEach((block) => {
      const config = blockTypeConfig[block.type] || blockTypeConfig.text;
      (block.connections || []).forEach((conn) => {
        const target = lookup.get(conn.to_block_id);
        if (!target) return;
        const startX = block.position.x + BLOCK_WIDTH / 2;
        const startY = block.position.y + BLOCK_HEIGHT;
        const endX = target.position.x + BLOCK_WIDTH / 2;
        const endY = target.position.y;

        // Calculate control points for smoother curves
        const deltaY = endY - startY;
        const controlOffset = Math.min(Math.abs(deltaY) * 0.5, 100);

        paths.push({
          id: `${block.id}-${conn.connection_id}`,
          d: `M ${startX} ${startY} C ${startX} ${startY + controlOffset}, ${endX} ${endY - controlOffset}, ${endX} ${endY}`,
          color: config.color,
          fromBlock: block.id,
          toBlock: conn.to_block_id,
          trigger: conn.trigger,
        });
      });
    });
    return paths;
  }, [blocks]);

  const renderBlockPreview = (block) => {
    if (!block.message) return 'No message yet';
    if (block.message.body) return block.message.body.slice(0, 48);
    if (block.message.url) return block.message.url.slice(0, 48);
    if (block.message.metadata?.contacts?.length) {
      return `Contacts: ${block.message.metadata.contacts.length}`;
    }
    if (block.message.metadata?.cards?.length) {
      return `Carousel: ${block.message.metadata.cards.length} cards`;
    }
    if (block.message.metadata?.latitude) {
      return `Location: ${block.message.metadata.latitude}, ${block.message.metadata.longitude}`;
    }
    if (block.message.metadata?.name) return `Template: ${block.message.metadata.name}`;
    return 'No preview';
  };

  const contactFieldSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: panelTint,
      borderRadius: 2,
      '& fieldset': { borderColor: 'rgba(148,163,184,0.15)' },
      '&:hover fieldset': { borderColor: 'rgba(148,163,184,0.3)' },
      '&.Mui-focused fieldset': {
        borderColor: (blockTypeConfig[selectedBlock?.type] || blockTypeConfig.text).color,
      },
    },
    '& .MuiInputBase-input': { color: textPrimary },
    '& .MuiInputLabel-root': { color: textSecondary },
  };

  const handleAddContact = () => {
    setEditor((prev) => ({
      ...prev,
      contacts: [
        ...(prev.contacts || []),
        {
          name: {
            formatted_name: '',
            first_name: '',
            last_name: '',
          },
          org: { company: '', department: '', title: '' },
          birthday: '',
          phones: [],
          emails: [],
          addresses: [],
          urls: [],
        },
      ],
    }));
  };

  const handleRemoveContact = (index) => {
    setEditor((prev) => ({
      ...prev,
      contacts: (prev.contacts || []).filter((_, idx) => idx !== index),
    }));
  };

  const handleUpdateContactField = (index, field, value) => {
    setEditor((prev) => {
      const next = [...(prev.contacts || [])];
      const current = next[index] || {};
      next[index] = { ...current, [field]: value };
      return { ...prev, contacts: next };
    });
  };

  useEffect(() => {
    if (selectedBlock?.type !== 'faq_categories') return;
    let active = true;
    faqApi
      .getCategories()
      .then((response) => {
        const data = response?.data?.data || response?.data || [];
        if (!active) return;
        setFaqCategories(Array.isArray(data) ? data.map(normalizeFaqCategory) : []);
      })
      .catch(() => {
        if (active) {
          setFaqCategories([]);
        }
      });
    return () => {
      active = false;
    };
  }, [selectedBlock?.type]);

  useEffect(() => {
    if (selectedBlock?.type !== 'faq_categories') return;
    let active = true;
    faqApi
      .getAll()
      .then((response) => {
        const data = response?.data?.data || response?.data || [];
        if (!active) return;
        setFaqItems(Array.isArray(data) ? data.map(normalizeFaqItem) : []);
      })
      .catch(() => {
        if (active) {
          setFaqItems([]);
        }
      });
    return () => {
      active = false;
    };
  }, [selectedBlock?.type]);

  const handleUpdateContactName = (index, field, value) => {
    setEditor((prev) => {
      const next = [...(prev.contacts || [])];
      const current = next[index] || {};
      const name = { ...(current.name || {}) };
      name[field] = value;
      next[index] = { ...current, name };
      return { ...prev, contacts: next };
    });
  };

  const handleUpdateContactOrg = (index, field, value) => {
    setEditor((prev) => {
      const next = [...(prev.contacts || [])];
      const current = next[index] || {};
      const org = { ...(current.org || {}) };
      org[field] = value;
      next[index] = { ...current, org };
      return { ...prev, contacts: next };
    });
  };

  const handleAddContactListItem = (index, listName, item) => {
    setEditor((prev) => {
      const next = [...(prev.contacts || [])];
      const current = next[index] || {};
      const list = Array.isArray(current[listName]) ? current[listName] : [];
      if (['phones', 'emails', 'addresses'].includes(listName) && list.length >= 1) {
        return prev;
      }
      next[index] = { ...current, [listName]: [...list, item] };
      return { ...prev, contacts: next };
    });
  };

  const handleUpdateContactListItem = (index, listName, itemIndex, field, value) => {
    setEditor((prev) => {
      const next = [...(prev.contacts || [])];
      const current = next[index] || {};
      const list = Array.isArray(current[listName]) ? [...current[listName]] : [];
      const item = { ...(list[itemIndex] || {}) };
      item[field] = value;
      list[itemIndex] = item;
      next[index] = { ...current, [listName]: list };
      return { ...prev, contacts: next };
    });
  };

  const handleRemoveContactListItem = (index, listName, itemIndex) => {
    setEditor((prev) => {
      const next = [...(prev.contacts || [])];
      const current = next[index] || {};
      const list = Array.isArray(current[listName]) ? current[listName] : [];
      next[index] = { ...current, [listName]: list.filter((_, idx) => idx !== itemIndex) };
      return { ...prev, contacts: next };
    });
  };

  const handleAddCarouselCard = () => {
    setEditor((prev) => ({
      ...prev,
      carouselCards: [
        ...(prev.carouselCards || []),
        {
          header: { type: 'image', link: '' },
          body: '',
          button: { text: '', url: '' },
        },
      ],
    }));
  };

  const handleRemoveCarouselCard = (index) => {
    setEditor((prev) => ({
      ...prev,
      carouselCards: (prev.carouselCards || []).filter((_, idx) => idx !== index),
    }));
  };

  const handleUpdateCarouselCard = (index, field, value) => {
    setEditor((prev) => {
      const next = [...(prev.carouselCards || [])];
      const current = next[index] || {};
      next[index] = { ...current, [field]: value };
      return { ...prev, carouselCards: next };
    });
  };

  const handleUpdateCarouselHeader = (index, field, value) => {
    setEditor((prev) => {
      const next = [...(prev.carouselCards || [])];
      const current = next[index] || {};
      const header = { ...(current.header || {}) };
      header[field] = value;
      next[index] = { ...current, header };
      return { ...prev, carouselCards: next };
    });
  };

  const handleUpdateCarouselButton = (index, field, value) => {
    setEditor((prev) => {
      const next = [...(prev.carouselCards || [])];
      const current = next[index] || {};
      const button = { ...(current.button || {}) };
      button[field] = value;
      next[index] = { ...current, button };
      return { ...prev, carouselCards: next };
    });
  };

  const handleAddItem = () => {
    const limit =
      selectedBlock?.type === 'interactive_button'
        ? 3
        : selectedBlock?.type === 'interactive_list'
        ? 10
        : null;
    if (limit && editor.items.length >= limit) {
      setError(
        selectedBlock.type === 'interactive_button'
          ? 'Interactive buttons allow up to 3 options.'
          : 'Interactive lists allow up to 10 options.'
      );
      return;
    }
    const timestamp = Date.now();
    const botPrefix = currentBotId ? `bot_${currentBotId}` : 'bot';
    setEditor((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        { id: `${botPrefix}_${timestamp}`, title: 'New option', section: 'Options' },
      ],
    }));
  };

  const handleUpdateItem = (idx, field, value) => {
    setEditor((prev) => ({
      ...prev,
      items: prev.items.map((item, index) =>
        index === idx ? { ...item, [field]: value } : item
      ),
    }));
  };

  const handleRemoveItem = (idx) => {
    setEditor((prev) => ({
      ...prev,
      items: prev.items.filter((_, index) => index !== idx),
    }));
  };

  const handleBotMediaSelect = async (event) => {
    const file = event.target.files?.[0] || null;
    event.target.value = '';
    if (!file) return;

    const category = selectedBlock?.type || null;
    const validation = validateMediaFile(file, category);
    if (!validation.valid) {
      setError(validation.error || 'Unsupported file type.');
      return;
    }

    setBotMediaUploading(true);
    try {
      const response = await uploadApi.uploadBotMedia(file);
      const payload = response?.data?.data ?? response?.data ?? {};
      if (!payload?.url) {
        setError('Upload failed: missing URL.');
        return;
      }
      setEditor((prev) => ({ ...prev, url: payload.url }));
    } catch (error) {
      setError('Failed to upload media.');
    } finally {
      setBotMediaUploading(false);
    }
  };

  const handleCarouselHeaderUpload = async (index, event) => {
    const file = event.target.files?.[0] || null;
    event.target.value = '';
    if (!file) return;

    const headerType = editor.carouselCards?.[index]?.header?.type || 'image';
    const validation = validateMediaFile(file, headerType);
    if (!validation.valid) {
      setError(validation.error || 'Unsupported file type.');
      return;
    }

    setCarouselUploading((prev) => ({ ...prev, [index]: true }));
    try {
      const response = await uploadApi.uploadBotMedia(file);
      const payload = response?.data?.data ?? response?.data ?? {};
      if (!payload?.url) {
        setError('Upload failed: missing URL.');
        return;
      }
      setEditor((prev) => {
        const next = [...(prev.carouselCards || [])];
        const current = next[index] || {};
        const header = { ...(current.header || {}) };
        header.link = payload.url;
        next[index] = { ...current, header };
        return { ...prev, carouselCards: next };
      });
    } catch (error) {
      setError('Failed to upload media.');
    } finally {
      setCarouselUploading((prev) => ({ ...prev, [index]: false }));
    }
  };

  const handleCtaHeaderUpload = async (event) => {
    const file = event.target.files?.[0] || null;
    event.target.value = '';
    if (!file) return;

    const headerType = editor.ctaHeaderType || 'image';
    const validation = validateMediaFile(file, headerType);
    if (!validation.valid) {
      setError(validation.error || 'Unsupported file type.');
      return;
    }

    setCtaHeaderUploading(true);
    try {
      const response = await uploadApi.uploadBotMedia(file);
      const payload = response?.data?.data ?? response?.data ?? {};
      if (!payload?.url) {
        setError('Upload failed: missing URL.');
        return;
      }
      setEditor((prev) => ({ ...prev, ctaHeaderLink: payload.url }));
    } catch (error) {
      setError('Failed to upload media.');
    } finally {
      setCtaHeaderUploading(false);
    }
  };

  const handleButtonHeaderUpload = async (event) => {
    const file = event.target.files?.[0] || null;
    event.target.value = '';
    if (!file) return;

    const headerType = editor.buttonHeaderType || 'image';
    const validation = validateMediaFile(file, headerType);
    if (!validation.valid) {
      setError(validation.error || 'Unsupported file type.');
      return;
    }

    setButtonHeaderUploading(true);
    try {
      const response = await uploadApi.uploadBotMedia(file);
      const payload = response?.data?.data ?? response?.data ?? {};
      if (!payload?.url) {
        setError('Upload failed: missing URL.');
        return;
      }
      setEditor((prev) => ({ ...prev, buttonHeaderLink: payload.url }));
    } catch (error) {
      setError('Failed to upload media.');
    } finally {
      setButtonHeaderUploading(false);
    }
  };

  const handleConnectFromPanel = async () => {
    if (!selectedBlock || !editor.connectTo) return;
    if (selectedBlock.type === 'live_chat') {
      setError('Live Chat blocks cannot connect to another block.');
      return;
    }
    const triggerValue = editor.trigger === 'auto' ? null : editor.trigger || null;
    try {
      const response = await botsApi.createWaConnection(currentBotId, {
        from_block_id: selectedBlock.id,
        to_block_id: Number(editor.connectTo),
        action_value: triggerValue,
      });
      const created = response?.data?.data || response?.data;
      setBlocks((prev) =>
        prev.map((block) => {
          if (block.id !== selectedBlock.id) return block;
          const triggerKey = triggerValue || 'auto';
          const cleaned = (block.connections || []).filter((c) => c.trigger !== triggerKey);
          return {
            ...block,
            connections: [
              ...cleaned,
              {
                connection_id: created?.id,
                to_block_id: Number(editor.connectTo),
                trigger: triggerKey,
              },
            ],
          };
        })
      );
      setEditor((prev) => ({ ...prev, connectTo: '' }));
    } catch (err) {
      setError('Failed to create connection.');
    }
  };

  const builderHeader = (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={2}
      alignItems={{ xs: 'stretch', md: 'center' }}
      justifyContent="space-between"
      sx={{
        px: 3,
        py: 2,
        borderBottom: `1px solid ${borderTone}`,
        background: headerBg,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Button
          variant="outlined"
          startIcon={<ArrowLeft size={16} />}
          onClick={() => navigate('/bots')}
          sx={{ borderColor: alpha(textPrimary, 0.4), color: textPrimary }}
        >
          Back
        </Button>
        <Typography variant="h6" sx={{ color: textPrimary, fontWeight: 700 }}>
          WhatsApp Flow Builder
        </Typography>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel sx={{ color: textSecondary }}>Select Bot</InputLabel>
          <Select
            value={currentBotId}
            label="Select Bot"
            onChange={(e) => setCurrentBotId(String(e.target.value))}
            sx={{
              backgroundColor: surface,
              color: textPrimary,
              borderRadius: 2,
              '& .MuiSvgIcon-root': { color: textSecondary },
            }}
          >
            {bots.map((bot) => (
              <MenuItem key={bot.bid} value={bot.bid}>
                {bot.b_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="outlined"
          startIcon={<RefreshCcw size={16} />}
          onClick={() => loadFlow(currentBotId)}
          sx={{ borderColor: alpha(textPrimary, 0.35), color: textPrimary }}
        >
          Refresh
        </Button>
      </Stack>
    </Stack>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: surfaceAlt,
        color: textPrimary,
        fontFamily: '"Space Grotesk", "Segoe UI", sans-serif',
      }}
    >
      {builderHeader}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          sx={{ height: { xs: 'auto', lg: 'calc(100vh - 88px)' } }}
        >
          {isLibraryOpen && (
            <Box
              sx={{
                width: { xs: '100%', lg: 280 },
                borderRight: `1px solid ${borderTone}`,
                background: surface,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                minHeight: 0,
                flex: '0 0 280px',
              }}
            >
              {/* Sidebar Header */}
              <Box
                sx={{
                  px: 2.5,
                  py: 2,
                  borderBottom: `1px solid ${borderTone}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 1,
                }}
              >
                <Box>
                  <Typography variant="subtitle1" sx={{ color: textPrimary, fontWeight: 700 }}>
                    Block Library
                  </Typography>
                  <Typography variant="caption" sx={{ color: textSecondary }}>
                    Drag blocks to canvas or click to add
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => setIsLibraryOpen(false)}
                  sx={{ color: textSecondary }}
                  aria-label="Collapse block library"
                >
                  <X size={18} />
                </IconButton>
              </Box>

              <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
                <Collapse in={isLibraryOpen} timeout="auto" unmountOnExit>
                  <Box sx={{ px: 2, py: 2.5 }}>
                      {/* Message Blocks Section */}
                      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
                        <Typography
                          variant="overline"
                          sx={{ color: textSecondary, fontWeight: 600, letterSpacing: 1.2 }}
                        >
                          Messages
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => setIsMessagesOpen((prev) => !prev)}
                          sx={{ color: textSecondary }}
                          aria-label={isMessagesOpen ? 'Collapse messages' : 'Expand messages'}
                        >
                          {isMessagesOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                        </IconButton>
                      </Stack>
                      <Collapse in={isMessagesOpen} timeout="auto" unmountOnExit>
                        <Stack spacing={1} mb={3}>
                          {messageBlocks.map((block) => {
                            const config = blockTypeConfig[block.type];
                            return (
                              <Button
                                key={block.type}
                                onClick={() => handleCreateBlock(block.type)}
                                sx={{
                                  justifyContent: 'flex-start',
                                  textTransform: 'none',
                                  borderRadius: 2,
                                  px: 1.5,
                                  py: 1,
                                  background: panelTint,
                                  border: `1px solid ${cardBorder}`,
                                  color: textPrimary,
                                  transition: 'all 0.2s ease',
                                  '&:hover': {
                                    background: config.lightBg,
                                    borderColor: config.color,
                                    transform: 'translateX(4px)',
                                  },
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: 1.5,
                                    background: config.lightBg,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    mr: 1.5,
                                  }}
                                >
                                  <block.icon size={16} color={config.color} />
                                </Box>
                                <Box sx={{ textAlign: 'left' }}>
                                  <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                                    {block.label}
                                  </Typography>
                                </Box>
                              </Button>
                            );
                          })}
                        </Stack>
                      </Collapse>

                      {/* Interactive Blocks Section */}
                      <Typography
                        variant="overline"
                        sx={{ color: textSecondary, fontWeight: 600, letterSpacing: 1.2, mb: 1.5, display: 'block' }}
                      >
                        Interactive
                      </Typography>
                      <Stack spacing={1} mb={3}>
                        {interactiveBlocks.map((block) => {
                          const config = blockTypeConfig[block.type];
                          return (
                            <Button
                              key={block.type}
                              onClick={() => handleCreateBlock(block.type)}
                              sx={{
                                justifyContent: 'flex-start',
                                textTransform: 'none',
                                borderRadius: 2,
                                px: 1.5,
                                py: 1,
                                background: panelTint,
                                border: `1px solid ${cardBorder}`,
                                color: textPrimary,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  background: config.lightBg,
                                  borderColor: config.color,
                                  transform: 'translateX(4px)',
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: 1.5,
                                  background: config.lightBg,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  mr: 1.5,
                                }}
                              >
                                <block.icon size={16} color={config.color} />
                              </Box>
                              <Box sx={{ textAlign: 'left' }}>
                                <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                                  {block.label}
                                </Typography>
                              </Box>
                            </Button>
                          );
                        })}
                      </Stack>

                      {/* Special Blocks Section */}
                      <Typography
                        variant="overline"
                        sx={{ color: textSecondary, fontWeight: 600, letterSpacing: 1.2, mb: 1.5, display: 'block' }}
                      >
                        Special
                      </Typography>
                      <Stack spacing={1}>
                        {specialBlocks.map((block) => {
                          const config = blockTypeConfig[block.type];
                          return (
                            <Button
                              key={block.type}
                              onClick={() => handleCreateBlock(block.type)}
                              sx={{
                                justifyContent: 'flex-start',
                                textTransform: 'none',
                                borderRadius: 2,
                                px: 1.5,
                                py: 1,
                                background: panelTint,
                                border: `1px solid ${cardBorder}`,
                                color: textPrimary,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  background: config.lightBg,
                                  borderColor: config.color,
                                  transform: 'translateX(4px)',
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  width: 32,
                                  height: 32,
                                  borderRadius: 1.5,
                                  background: config.lightBg,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  mr: 1.5,
                                }}
                              >
                                <block.icon size={16} color={config.color} />
                              </Box>
                              <Box sx={{ textAlign: 'left' }}>
                                <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
                                  {block.label}
                                </Typography>
                              </Box>
                            </Button>
                          );
                        })}
                      </Stack>
                  </Box>
                </Collapse>
              </Box>

              {/* Sidebar Footer */}
              <Box
                sx={{
                  px: 2,
                  py: 2,
                  borderTop: `1px solid ${borderTone}`,
                  background: surface,
                }}
              >
                <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography variant="overline" sx={{ color: textSecondary, fontWeight: 600, letterSpacing: 1 }}>
                    Tips
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setIsTipsOpen((prev) => !prev)}
                    sx={{ color: textSecondary }}
                    aria-label={isTipsOpen ? 'Collapse tips' : 'Expand tips'}
                  >
                    {isTipsOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  </IconButton>
                </Stack>
                <Collapse in={isTipsOpen} timeout="auto" unmountOnExit>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1.25,
                      borderRadius: 2,
                      background: surfaceAlt,
                      border: `1px solid ${cardBorder}`,
                      boxShadow: cardShadow,
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{ color: textSecondary, display: 'block', mb: 0.5, lineHeight: 1.4 }}
                    >
                      Tip: Hold Ctrl + scroll to zoom
                    </Typography>
                    <Typography variant="caption" sx={{ color: textSecondary, lineHeight: 1.4 }}>
                      Click and drag canvas to pan
                    </Typography>
                  </Box>
                </Collapse>
              </Box>
            </Box>
          )}

          <Box
            sx={{
              flex: 1,
              position: 'relative',
              overflow: 'hidden',
              minHeight: { xs: 520, lg: 'auto' },
            }}
          >
            {!isLibraryOpen && (
              <IconButton
                size="small"
                onClick={() => setIsLibraryOpen(true)}
                sx={{
                  position: 'absolute',
                  top: 16,
                  left: 16,
                  zIndex: 20,
                  background: surface,
                  border: `1px solid ${cardBorder}`,
                  color: textSecondary,
                  boxShadow: '0 6px 16px rgba(15, 23, 42, 0.12)',
                  '&:hover': {
                    color: palette.primary.main,
                    borderColor: palette.primary.main,
                    background: surface,
                  },
                }}
                aria-label="Open block library"
              >
                <ChevronRight size={18} />
              </IconButton>
            )}
            {!isInspectorOpen && (
              <IconButton
                size="small"
                onClick={() => setIsInspectorOpen(true)}
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  zIndex: 16,
                  background: surface,
                  border: `1px solid ${cardBorder}`,
                  boxShadow: cardShadow,
                  color: textSecondary,
                  '&:hover': { background: panelTint },
                }}
                aria-label="Open block inspector"
              >
                <ChevronLeft size={18} />
              </IconButton>
            )}
            {error && (
              <Alert
                severity="error"
                sx={{
                  position: 'absolute',
                  top: 16,
                  left: 16,
                  zIndex: 20,
                  borderRadius: 2,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                }}
              >
                {error}
              </Alert>
            )}

            {/* Zoom Controls */}
            <Stack
              direction="row"
              spacing={0.5}
              sx={{
                position: 'absolute',
                bottom: 20,
                left: 20,
                zIndex: 15,
                background: surface,
                borderRadius: 2,
                p: 0.5,
                boxShadow: cardShadow,
                border: `1px solid ${cardBorder}`,
              }}
            >
              <Tooltip title="Zoom Out">
                <IconButton size="small" onClick={handleZoomOut} sx={{ color: textSecondary }}>
                  <ZoomOut size={18} />
                </IconButton>
              </Tooltip>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: 1,
                  minWidth: 50,
                  justifyContent: 'center',
                }}
              >
                <Typography variant="caption" sx={{ color: textPrimary, fontWeight: 600 }}>
                  {Math.round(zoom * 100)}%
                </Typography>
              </Box>
              <Tooltip title="Zoom In">
                <IconButton size="small" onClick={handleZoomIn} sx={{ color: textSecondary }}>
                  <ZoomIn size={18} />
                </IconButton>
              </Tooltip>
              <Divider orientation="vertical" flexItem sx={{ borderColor: borderTone }} />
              <Tooltip title="Reset View">
                <IconButton size="small" onClick={handleResetView} sx={{ color: textSecondary }}>
                  <Maximize2 size={18} />
                </IconButton>
              </Tooltip>
            </Stack>

            {/* Minimap */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 20,
                right: 20,
                zIndex: 15,
                width: 180,
                height: 120,
                background: surface,
                borderRadius: 2,
                border: `1px solid ${cardBorder}`,
                boxShadow: cardShadow,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  position: 'relative',
                  backgroundImage: `radial-gradient(circle, ${alpha(textPrimary, 0.12)} 1px, transparent 1px)`,
                  backgroundSize: '8px 8px',
                }}
              >
                {blocks.map((block) => {
                  const config = blockTypeConfig[block.type] || blockTypeConfig.text;
                  return (
                    <Box
                      key={`mini-${block.id}`}
                      sx={{
                        position: 'absolute',
                        left: `${(block.position.x / 3000) * 100}%`,
                        top: `${(block.position.y / 2000) * 100}%`,
                        width: 12,
                        height: 8,
                        borderRadius: 0.5,
                        background: block.id === selectedBlockId ? config.color : alpha(textPrimary, 0.35),
                        border: block.id === selectedBlockId ? `1px solid ${config.color}` : 'none',
                        transition: 'background 0.2s ease',
                      }}
                    />
                  );
                })}
                {/* Viewport indicator */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: `${Math.max(0, (-pan.x / zoom / 3000) * 100)}%`,
                    top: `${Math.max(0, (-pan.y / zoom / 2000) * 100)}%`,
                    width: `${Math.min(100, (100 / zoom))}%`,
                    height: `${Math.min(100, (100 / zoom))}%`,
                    border: '1px solid rgba(56, 189, 248, 0.5)',
                    borderRadius: 1,
                    background: 'rgba(56, 189, 248, 0.1)',
                    pointerEvents: 'none',
                  }}
                />
              </Box>
            </Box>

            {/* Connection mode indicator */}
            {connecting && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 20,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 15,
                  background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                  borderRadius: 3,
                  px: 3,
                  py: 1,
                  boxShadow: '0 4px 20px rgba(34, 197, 94, 0.4)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Cable size={16} />
                <Typography variant="body2" sx={{ color: '#fff', fontWeight: 600 }}>
                  Click a block to connect
                </Typography>
                <Button
                  size="small"
                  onClick={() => setConnecting(null)}
                  sx={{ color: '#fff', ml: 1, minWidth: 'auto' }}
                >
                  Cancel
                </Button>
              </Box>
            )}

            <Box
              ref={canvasRef}
              onMouseDown={handleCanvasMouseDown}
              onWheel={handleWheel}
              sx={{
                position: 'relative',
                width: '100%',
                height: '100%',
                backgroundColor: surfaceAlt,
                backgroundImage: `radial-gradient(circle, ${canvasDot} 1px, transparent 1px)`,
                backgroundSize: `${24 * zoom}px ${24 * zoom}px`,
                backgroundPosition: `${pan.x}px ${pan.y}px`,
                cursor: isPanning ? 'grabbing' : connecting ? 'crosshair' : 'grab',
              }}
              onClick={() => {
                if (isPanning) return;
                setSelectedBlockId(null);
                setIsInspectorOpen(false);
              }}
            >
              {/* Canvas transform container */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                  transformOrigin: '0 0',
                }}
              >
                {/* SVG for connections */}
                <svg
                  width="3000"
                  height="2000"
                  style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none' }}
                >
                  <defs>
                    {/* Arrow marker definitions for each color */}
                    {Object.entries(blockTypeConfig).map(([type, config]) => (
                      <marker
                        key={`arrow-${type}`}
                        id={`arrow-${type}`}
                        viewBox="0 0 10 10"
                        refX="8"
                        refY="5"
                        markerWidth="6"
                        markerHeight="6"
                        orient="auto-start-reverse"
                      >
                        <path d="M 0 0 L 10 5 L 0 10 z" fill={config.color} />
                      </marker>
                    ))}
                    {/* Glow filter */}
                    <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                      <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                      <feMerge>
                        <feMergeNode in="coloredBlur" />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                  </defs>

                  {connectionPaths.map((path) => (
                    <g key={path.id}>
                      {/* Shadow/glow path */}
                      <path
                        d={path.d}
                        stroke={path.color}
                        strokeWidth={4}
                        fill="none"
                        strokeOpacity={0.3}
                        filter="url(#glow)"
                      />
                      {/* Main path */}
                      <path
                        d={path.d}
                        stroke={path.color}
                        strokeWidth={2.5}
                        fill="none"
                        strokeLinecap="round"
                        markerEnd={`url(#arrow-${blocks.find((b) => b.id === path.fromBlock)?.type || 'text'})`}
                        style={{
                          transition: 'stroke 0.2s ease',
                        }}
                      />
                    </g>
                  ))}
                </svg>

                {flowLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', pt: 10 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  blocks.map((block) => {
                    const config = blockTypeConfig[block.type] || blockTypeConfig.text;
                    const BlockIcon = config.icon;
                    const isSelected = block.id === selectedBlockId;

                    return (
                      <Box
                        key={block.id}
                        onMouseDown={(event) => {
                          if (event.target.closest?.('[data-no-drag="true"]')) return;
                          handleMouseDown(event, block.id);
                        }}
                        onClick={(event) => {
                          event.stopPropagation();
                          if (connecting) {
                            handleTargetConnection(block.id);
                          } else {
                            handleSelectBlock(block.id);
                          }
                        }}
                        sx={{
                          position: 'absolute',
                          left: block.position.x,
                          top: block.position.y,
                          width: BLOCK_WIDTH,
                          minHeight: BLOCK_HEIGHT,
                          borderRadius: 3,
                          border: isSelected
                            ? `2px solid ${config.color}`
                            : `1px solid ${cardBorder}`,
                          background: surface,
                          boxShadow: isSelected
                            ? `0 0 0 4px ${config.lightBg}, ${cardShadowActive}`
                            : cardShadow,
                          cursor: connecting ? 'crosshair' : 'grab',
                          transition: 'box-shadow 0.2s ease, border-color 0.2s ease',
                          overflow: 'hidden',
                          '&:hover': {
                            borderColor: config.color,
                            boxShadow: `0 0 0 2px ${config.lightBg}, ${cardShadow}`,
                          },
                        }}
                      >
                        {/* Block Header with gradient accent */}
                        <Box
                          sx={{
                            background: config.gradient,
                            height: 4,
                          }}
                        />

                        {/* Start block indicator */}
                        {block.is_start && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                              borderRadius: '50%',
                              width: 24,
                              height: 24,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: '0 2px 8px rgba(34, 197, 94, 0.4)',
                            }}
                          >
                            <Play size={12} fill="#fff" color="#fff" />
                          </Box>
                        )}

                        {/* Block content */}
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="flex-start"
                          sx={{ p: 2 }}
                        >
                          {/* Icon container */}
                          <Box
                            sx={{
                              width: 36,
                              height: 36,
                              borderRadius: 2,
                              background: config.lightBg,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <BlockIcon size={18} color={config.color} />
                          </Box>

                          {/* Text content */}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 600,
                                color: textPrimary,
                                mb: 0.25,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {block.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: config.color,
                                fontWeight: 500,
                                textTransform: 'capitalize',
                              }}
                            >
                              {block.type.replace('_', ' ')}
                            </Typography>
                          </Box>

                          {/* Drag handle removed from header */}
                        </Stack>

                        {/* Edit button */}
                        <IconButton
                          size="small"
                          data-no-drag="true"
                          onMouseDown={(event) => event.stopPropagation()}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleOpenBlockEditor(block.id);
                          }}
                          sx={{
                            position: 'absolute',
                            top: 6,
                            right: block.is_start ? 36 : 8,
                            width: 28,
                            height: 28,
                            borderRadius: 2,
                            background: surface,
                            border: `1px solid ${cardBorder}`,
                            color: textSecondary,
                            boxShadow: '0 4px 12px rgba(15, 23, 42, 0.12)',
                            cursor: 'pointer',
                            '&:hover': {
                              color: config.color,
                              borderColor: config.color,
                              background: surface,
                            },
                          }}
                        >
                          <Pencil size={14} />
                        </IconButton>

                        {/* Preview text */}
                        <Box sx={{ px: 2, pb: 1.5 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: textSecondary,
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                            }}
                          >
                            {renderBlockPreview(block)}
                          </Typography>
                        </Box>

                        {/* Connection output port */}
                        {block.type !== 'live_chat' &&
                          block.type !== 'appointment' &&
                          !['interactive_button', 'interactive_list', 'carousel', 'faq_categories'].includes(block.type) && (
                          <Tooltip title="Click to connect" placement="bottom">
                            <Box
                              data-no-drag="true"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleStartConnection(block.id, null);
                              }}
                              onMouseDown={(event) => event.stopPropagation()}
                              sx={{
                                position: 'absolute',
                                bottom: -13,
                                left: '50%',
                                transform: 'translateX(-50%)',
                                width: 26,
                                height: 26,
                                borderRadius: '50%',
                                background: config.gradient,
                                border: `3px solid ${surfaceAlt}`,
                                cursor: 'pointer',
                                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                boxShadow: `0 2px 8px ${config.color}40`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                '&:hover': {
                                  transform: 'translateX(-50%) scale(1.2)',
                                  boxShadow: `0 4px 12px ${config.color}60`,
                                },
                              }}
                            />
                          </Tooltip>
                        )}

                        {/* Connection input port */}
                        {!['interactive_button', 'interactive_list'].includes(block.type) && (
                          <Box
                            data-no-drag="true"
                            sx={{
                              position: 'absolute',
                              top: -8,
                              left: '50%',
                              transform: 'translateX(-50%)',
                              width: 16,
                              height: 16,
                              borderRadius: '50%',
                              background: connecting ? config.color : 'rgba(148,163,184,0.3)',
                              border: `2px solid ${surfaceAlt}`,
                              transition: 'background 0.2s ease',
                            }}
                          />
                        )}

                        {/* Interactive buttons */}
                        {block.type === 'interactive_button' && block.message?.buttons?.length > 0 && (
                          <Box sx={{ px: 2, pb: 2 }}>
                            <Stack spacing={0.75}>
                              {block.message.buttons.map((btn) => (
                                <Stack
                                  key={btn.id}
                                  direction="row"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  sx={{
                                    px: 1.5,
                                    py: 0.75,
                                    borderRadius: 1.5,
                                    background: 'rgba(249, 115, 22, 0.1)',
                                    border: '1px solid rgba(249, 115, 22, 0.2)',
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{ color: '#f97316', fontWeight: 500 }}
                                  >
                                    {btn.title}
                                  </Typography>
                                  <Tooltip title={`Connect from "${btn.title}"`}>
                                    <Box
                                      data-no-drag="true"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        handleStartConnection(block.id, btn.id);
                                      }}
                                      onMouseDown={(event) => event.stopPropagation()}
                                      sx={{
                                        width: 18,
                                        height: 18,
                                        borderRadius: '50%',
                                        background: '#f97316',
                                        border: `2px solid ${surfaceAlt}`,
                                        cursor: 'crosshair',
                                        transition: 'transform 0.2s ease',
                                        '&:hover': { transform: 'scale(1.3)' },
                                      }}
                                    />
                                  </Tooltip>
                                </Stack>
                              ))}
                            </Stack>
                          </Box>
                        )}

                        {/* Interactive list rows */}
                        {block.type === 'interactive_list' && block.message?.rows?.length > 0 && (
                          <Box sx={{ px: 2, pb: 2 }}>
                            <Stack spacing={0.75}>
                              {block.message.rows.map((row) => (
                                <Stack
                                  key={row.id}
                                  direction="row"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  sx={{
                                    px: 1.5,
                                    py: 0.75,
                                    borderRadius: 1.5,
                                    background: 'rgba(6, 182, 212, 0.1)',
                                    border: '1px solid rgba(6, 182, 212, 0.2)',
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{ color: '#06b6d4', fontWeight: 500 }}
                                  >
                                    {row.title}
                                  </Typography>
                                  <Tooltip title={`Connect from "${row.title}"`}>
                                    <Box
                                      data-no-drag="true"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        handleStartConnection(block.id, row.id);
                                      }}
                                      onMouseDown={(event) => event.stopPropagation()}
                                      sx={{
                                        width: 18,
                                        height: 18,
                                        borderRadius: '50%',
                                        background: '#06b6d4',
                                        border: `2px solid ${surfaceAlt}`,
                                        cursor: 'crosshair',
                                        transition: 'transform 0.2s ease',
                                        '&:hover': { transform: 'scale(1.3)' },
                                      }}
                                    />
                                  </Tooltip>
                                </Stack>
                              ))}
                            </Stack>
                          </Box>
                        )}
                      </Box>
                    );
                  })
                )}
              </Box>
            </Box>
          </Box>

          {isInspectorOpen && (
            <Box
              sx={{
                width: { xs: '100%', lg: 340 },
                borderLeft: { xs: 'none', lg: `1px solid ${borderTone}` },
                background: surface,
                overflowY: 'auto',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
            {!selectedBlock ? (
              <Box sx={{ position: 'relative', flex: 1 }}>
                <IconButton
                  size="small"
                  onClick={() => setIsInspectorOpen(false)}
                  sx={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    color: textSecondary,
                  }}
                  aria-label="Close block inspector"
                >
                  <X size={18} />
                </IconButton>
                <Stack
                  spacing={2}
                  alignItems="center"
                  justifyContent="center"
                  sx={{ flex: 1, px: 3, height: '100%' }}
                >
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: 3,
                    background: panelTint,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <MessageSquareText size={32} color={textSecondary} />
                </Box>
                <Typography variant="h6" sx={{ color: textSecondary, fontWeight: 600 }}>
                  No Block Selected
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: textSecondary, textAlign: 'center', maxWidth: 240 }}
                >
                  Select a block from the canvas to edit its properties and connections
                </Typography>
                </Stack>
              </Box>
            ) : (
              <>
                {/* Panel Header */}
                <Box
                  sx={{
                    px: 2.5,
                    py: 2,
                    borderBottom: `1px solid ${borderTone}`,
                    background: (blockTypeConfig[selectedBlock.type] || blockTypeConfig.text).lightBg,
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 2,
                        background: (blockTypeConfig[selectedBlock.type] || blockTypeConfig.text).gradient,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {(() => {
                        const config = blockTypeConfig[selectedBlock.type] || blockTypeConfig.text;
                        const IconComponent = config.icon;
                        return <IconComponent size={20} color="#fff" />;
                      })()}
                    </Box>
                    <Box>
                      <Typography variant="subtitle1" sx={{ color: textPrimary, fontWeight: 700 }}>
                        Edit Block
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: (blockTypeConfig[selectedBlock.type] || blockTypeConfig.text).color,
                          fontWeight: 500,
                          textTransform: 'capitalize',
                        }}
                      >
                        {selectedBlock.type.replace('_', ' ')}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                {/* Panel Content */}
                <Box sx={{ px: 2.5, py: 2.5, flex: 1 }}>
                  <Stack spacing={2.5}>
                    {/* Block Name */}
                    <Box>
                      <Typography
                        variant="overline"
                        sx={{ color: textSecondary, fontWeight: 600, letterSpacing: 1, mb: 1, display: 'block' }}
                      >
                        Block Name
                      </Typography>
                      <TextField
                        value={editor.name}
                        onChange={(e) => setEditor((prev) => ({ ...prev, name: e.target.value }))}
                        size="small"
                        fullWidth
                        placeholder="Enter block name"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: panelTint,
                            borderRadius: 2,
                            '& fieldset': { borderColor: cardBorder },
                            '&:hover fieldset': { borderColor: alpha(textPrimary, 0.3) },
                            '&.Mui-focused fieldset': {
                              borderColor: (blockTypeConfig[selectedBlock.type] || blockTypeConfig.text).color,
                            },
                          },
                          '& .MuiInputBase-input': { color: textPrimary },
                        }}
                      />
                    </Box>

                    {/* Start Block Toggle */}
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        background: editor.isStart
                          ? 'rgba(34, 197, 94, 0.1)'
                          : panelTint,
                        border: editor.isStart
                          ? '1px solid rgba(34, 197, 94, 0.3)'
                          : `1px solid ${cardBorder}`,
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Play
                          size={16}
                          color={editor.isStart ? '#22c55e' : textSecondary}
                          fill={editor.isStart ? '#22c55e' : 'none'}
                        />
                        <Typography
                          variant="body2"
                          sx={{ color: editor.isStart ? '#22c55e' : textSecondary, fontWeight: 500 }}
                        >
                          Start Block
                        </Typography>
                      </Stack>
                      <Button
                        variant={editor.isStart ? 'contained' : 'outlined'}
                        size="small"
                        onClick={() => setEditor((prev) => ({ ...prev, isStart: !prev.isStart }))}
                        sx={{
                          minWidth: 70,
                          borderRadius: 1.5,
                          textTransform: 'none',
                          ...(editor.isStart
                            ? {
                                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                                color: '#fff',
                              }
                            : {
                                borderColor: alpha(textPrimary, 0.3),
                                color: textSecondary,
                              }),
                        }}
                      >
                        {editor.isStart ? 'Active' : 'Set'}
                      </Button>
                    </Stack>

                    <Divider sx={{ borderColor: borderTone }} />

                    {/* Message Content */}
                    {['text', 'appointment', 'template', 'live_chat', 'location_request'].includes(selectedBlock.type) && (
                      <Box>
                        <Typography
                          variant="overline"
                          sx={{ color: textSecondary, fontWeight: 600, letterSpacing: 1, mb: 1, display: 'block' }}
                        >
                          Message Content
                        </Typography>
                        <TextField
                          value={editor.body}
                          onChange={(e) => setEditor((prev) => ({ ...prev, body: e.target.value }))}
                          multiline
                          minRows={4}
                          size="small"
                          fullWidth
                          placeholder="Enter your message here..."
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: panelTint,
                              borderRadius: 2,
                              '& fieldset': { borderColor: 'rgba(148,163,184,0.15)' },
                              '&:hover fieldset': { borderColor: 'rgba(148,163,184,0.3)' },
                              '&.Mui-focused fieldset': {
                                borderColor: (blockTypeConfig[selectedBlock.type] || blockTypeConfig.text).color,
                              },
                            },
                            '& .MuiInputBase-input': { color: textPrimary },
                          }}
                        />
                      </Box>
                    )}

                    {selectedBlock.type === 'appointment' && (
                      <Box>
                        <Typography
                          variant="overline"
                          sx={{ color: textSecondary, fontWeight: 600, letterSpacing: 1, mb: 1, display: 'block' }}
                        >
                          Appointment Flow
                        </Typography>
                        <Typography variant="body2" sx={{ color: textSecondary, mb: 1.5 }}>
                          Open the appointment flow page to collect branch, service, employee, and slot details.
                        </Typography>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => navigate('/appointments')}
                          sx={{
                            textTransform: 'none',
                            borderRadius: 2,
                            background: (blockTypeConfig[selectedBlock.type] || blockTypeConfig.text).gradient,
                          }}
                        >
                          Open Appointments
                        </Button>
                      </Box>
                    )}

                    {/* Media URL */}
                    {['image', 'video', 'audio', 'document'].includes(selectedBlock.type) && (
                      <Box>
                        <Typography
                          variant="overline"
                          sx={{ color: textSecondary, fontWeight: 600, letterSpacing: 1, mb: 1, display: 'block' }}
                        >
                          Media URL
                        </Typography>
                        <Stack spacing={1.5}>
                          <TextField
                            value={editor.url}
                            onChange={(e) => setEditor((prev) => ({ ...prev, url: e.target.value }))}
                            size="small"
                            fullWidth
                            placeholder="https://example.com/media.jpg"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: panelTint,
                                borderRadius: 2,
                                '& fieldset': { borderColor: 'rgba(148,163,184,0.15)' },
                                '&:hover fieldset': { borderColor: 'rgba(148,163,184,0.3)' },
                                '&.Mui-focused fieldset': {
                                  borderColor: (blockTypeConfig[selectedBlock.type] || blockTypeConfig.text).color,
                                },
                              },
                              '& .MuiInputBase-input': { color: textPrimary },
                            }}
                          />
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => botMediaInputRef.current?.click()}
                              disabled={botMediaUploading}
                              sx={{
                                textTransform: 'none',
                                color: (blockTypeConfig[selectedBlock.type] || blockTypeConfig.text).color,
                                borderColor: alpha(
                                  (blockTypeConfig[selectedBlock.type] || blockTypeConfig.text).color,
                                  0.5
                                ),
                              }}
                            >
                              {botMediaUploading ? 'Uploading...' : 'Upload Media'}
                            </Button>
                            <Typography variant="caption" sx={{ color: textSecondary }}>
                              {selectedBlock.type.toUpperCase()} only
                            </Typography>
                          </Stack>
                          <input
                            ref={botMediaInputRef}
                            type="file"
                            accept={FILE_ACCEPT_STRINGS[selectedBlock.type] || FILE_ACCEPT_STRINGS.all}
                            style={{ display: 'none' }}
                            onChange={handleBotMediaSelect}
                          />
                        </Stack>
                      </Box>
                    )}

                    {selectedBlock.type === 'address' && (
                      <Box>
                        <Typography
                          variant="overline"
                          sx={{ color: textSecondary, fontWeight: 600, letterSpacing: 1, mb: 1, display: 'block' }}
                        >
                          Address Request
                        </Typography>
                        <Stack spacing={1.5}>
                          <TextField
                            label="Body Text"
                            value={editor.body}
                            onChange={(e) => setEditor((prev) => ({ ...prev, body: e.target.value }))}
                            multiline
                            minRows={3}
                            size="small"
                            fullWidth
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: panelTint,
                                borderRadius: 2,
                                '& fieldset': { borderColor: 'rgba(148,163,184,0.15)' },
                                '&:hover fieldset': { borderColor: 'rgba(148,163,184,0.3)' },
                                '&.Mui-focused fieldset': {
                                  borderColor: (blockTypeConfig[selectedBlock.type] || blockTypeConfig.text).color,
                                },
                              },
                              '& .MuiInputBase-input': { color: textPrimary },
                              '& .MuiInputLabel-root': { color: textSecondary },
                            }}
                          />
                          <TextField
                            label="Country Code"
                            value={editor.addressCountry}
                            onChange={(e) => setEditor((prev) => ({ ...prev, addressCountry: e.target.value }))}
                            size="small"
                            fullWidth
                            placeholder="IN"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: panelTint,
                                borderRadius: 2,
                                '& fieldset': { borderColor: 'rgba(148,163,184,0.15)' },
                                '&:hover fieldset': { borderColor: 'rgba(148,163,184,0.3)' },
                                '&.Mui-focused fieldset': {
                                  borderColor: (blockTypeConfig[selectedBlock.type] || blockTypeConfig.text).color,
                                },
                              },
                              '& .MuiInputBase-input': { color: textPrimary },
                              '& .MuiInputLabel-root': { color: textSecondary },
                            }}
                          />
                        </Stack>
                      </Box>
                    )}

                    {selectedBlock.type === 'carousel' && (
                      <Box>
                        <Typography
                          variant="overline"
                          sx={{ color: textSecondary, fontWeight: 600, letterSpacing: 1, mb: 1, display: 'block' }}
                        >
                          Carousel
                        </Typography>
                        <Stack spacing={2}>
                          <TextField
                            label="Main Body Text"
                            value={editor.body}
                            onChange={(e) => setEditor((prev) => ({ ...prev, body: e.target.value }))}
                            multiline
                            minRows={3}
                            size="small"
                            fullWidth
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: panelTint,
                                borderRadius: 2,
                                '& fieldset': { borderColor: 'rgba(148,163,184,0.15)' },
                                '&:hover fieldset': { borderColor: 'rgba(148,163,184,0.3)' },
                                '&.Mui-focused fieldset': {
                                  borderColor: (blockTypeConfig[selectedBlock.type] || blockTypeConfig.text).color,
                                },
                              },
                              '& .MuiInputBase-input': { color: textPrimary },
                              '& .MuiInputLabel-root': { color: textSecondary },
                            }}
                          />
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="caption" sx={{ color: textSecondary }}>
                              Cards ({editor.carouselCards.length})
                            </Typography>
                            <Button
                              size="small"
                              startIcon={<Plus size={14} />}
                              onClick={handleAddCarouselCard}
                              disabled={editor.carouselCards.length >= 10}
                              sx={{ textTransform: 'none' }}
                            >
                              Add Card
                            </Button>
                          </Stack>
                          <Stack spacing={2}>
                            {editor.carouselCards.map((card, idx) => (
                              <Box
                                key={`carousel-${idx}`}
                                sx={{
                                  p: 2,
                                  borderRadius: 2,
                                  background: panelTint,
                                  border: `1px solid ${cardBorder}`,
                                }}
                              >
                                <Stack spacing={1.5}>
                                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                                    <Typography variant="subtitle2" sx={{ color: textPrimary }}>
                                      Card {idx + 1}
                                    </Typography>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleRemoveCarouselCard(idx)}
                                      sx={{ color: '#ef4444' }}
                                    >
                                      <Trash2 size={16} />
                                    </IconButton>
                                  </Stack>
                                  <Stack direction="row" spacing={1}>
                                    <FormControl size="small" sx={{ minWidth: 140 }}>
                                      <InputLabel sx={{ color: textSecondary }}>Header Type</InputLabel>
                                      <Select
                                        label="Header Type"
                                        value={card?.header?.type || 'image'}
                                        onChange={(e) =>
                                          handleUpdateCarouselHeader(idx, 'type', e.target.value)
                                        }
                                        sx={{
                                          backgroundColor: panelTint,
                                          borderRadius: 2,
                                          color: textPrimary,
                                          '& .MuiSvgIcon-root': { color: textSecondary },
                                          '& .MuiOutlinedInput-notchedOutline': {
                                            borderColor: 'rgba(148,163,184,0.15)',
                                          },
                                        }}
                                      >
                                        <MenuItem value="image">Image</MenuItem>
                                        <MenuItem value="video">Video</MenuItem>
                                      </Select>
                                    </FormControl>
                                    <TextField
                                      label="Header URL"
                                      value={card?.header?.link || ''}
                                      onChange={(e) =>
                                        handleUpdateCarouselHeader(idx, 'link', e.target.value)
                                      }
                                      size="small"
                                      fullWidth
                                      sx={contactFieldSx}
                                    />
                                  </Stack>
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <Button
                                      size="small"
                                      variant="outlined"
                                      onClick={() => carouselInputRefs.current[idx]?.click()}
                                      disabled={!!carouselUploading[idx]}
                                      sx={{
                                        textTransform: 'none',
                                        color: (blockTypeConfig[selectedBlock.type] || blockTypeConfig.text).color,
                                        borderColor: alpha(
                                          (blockTypeConfig[selectedBlock.type] || blockTypeConfig.text).color,
                                          0.5
                                        ),
                                      }}
                                    >
                                      {carouselUploading[idx] ? 'Uploading...' : 'Upload Media'}
                                    </Button>
                                    <Typography variant="caption" sx={{ color: textSecondary }}>
                                      {String(card?.header?.type || 'image').toUpperCase()} only
                                    </Typography>
                                    <input
                                      ref={(el) => {
                                        carouselInputRefs.current[idx] = el;
                                      }}
                                      type="file"
                                      accept={
                                        FILE_ACCEPT_STRINGS[card?.header?.type || 'image'] ||
                                        FILE_ACCEPT_STRINGS.all
                                      }
                                      style={{ display: 'none' }}
                                      onChange={(event) => handleCarouselHeaderUpload(idx, event)}
                                    />
                                  </Stack>
                                  <TextField
                                    label="Card Body Text"
                                    value={card?.body || ''}
                                    onChange={(e) => handleUpdateCarouselCard(idx, 'body', e.target.value)}
                                    multiline
                                    minRows={2}
                                    size="small"
                                    fullWidth
                                    sx={contactFieldSx}
                                  />
                                  <Stack direction="row" spacing={1}>
                                    <TextField
                                      label="Button Label"
                                      value={card?.button?.text || ''}
                                      onChange={(e) =>
                                        handleUpdateCarouselButton(idx, 'text', e.target.value)
                                      }
                                      size="small"
                                      fullWidth
                                      sx={contactFieldSx}
                                    />
                                    <TextField
                                      label="Button URL"
                                      value={card?.button?.url || ''}
                                      onChange={(e) =>
                                        handleUpdateCarouselButton(idx, 'url', e.target.value)
                                      }
                                      size="small"
                                      fullWidth
                                      sx={contactFieldSx}
                                    />
                                  </Stack>
                                </Stack>
                              </Box>
                            ))}
                          </Stack>
                        </Stack>
                      </Box>
                    )}

                    {selectedBlock.type === 'faq_categories' && (
                      <Box>
                        {/* Section Header */}
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            mb: 2.5,
                            pb: 1.5,
                            borderBottom: `1px solid ${alpha(blockTypeConfig.faq_categories.color, 0.2)}`,
                          }}
                        >
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: '10px',
                              background: blockTypeConfig.faq_categories.gradient,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxShadow: `0 4px 12px ${alpha(blockTypeConfig.faq_categories.color, 0.3)}`,
                            }}
                          >
                            <MessageSquareText size={16} color="#fff" />
                          </Box>
                          <Box>
                            <Typography
                              variant="subtitle2"
                              sx={{ color: textPrimary, fontWeight: 600, lineHeight: 1.2 }}
                            >
                              FAQ Categories
                            </Typography>
                            <Typography variant="caption" sx={{ color: textSecondary }}>
                              Configure FAQ display settings
                            </Typography>
                          </Box>
                        </Box>

                        <Stack spacing={2.5}>
                          {/* Content Section */}
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 2.5,
                              backgroundColor: alpha(blockTypeConfig.faq_categories.color, 0.04),
                              border: `1px solid ${alpha(blockTypeConfig.faq_categories.color, 0.1)}`,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: blockTypeConfig.faq_categories.color,
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                                mb: 1.5,
                                display: 'block',
                              }}
                            >
                              Content
                            </Typography>
                            <Stack spacing={1.5}>
                              <TextField
                                label="Header Text"
                                placeholder="Enter a header for your FAQ section"
                                value={editor.faqHeader}
                                onChange={(e) => setEditor((prev) => ({ ...prev, faqHeader: e.target.value }))}
                                size="small"
                                fullWidth
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: panelTint,
                                    borderRadius: 2,
                                    '& fieldset': { borderColor: 'rgba(148,163,184,0.15)' },
                                    '&:hover fieldset': { borderColor: alpha(blockTypeConfig.faq_categories.color, 0.4) },
                                    '&.Mui-focused fieldset': {
                                      borderColor: blockTypeConfig.faq_categories.color,
                                    },
                                  },
                                  '& .MuiInputBase-input': { color: textPrimary },
                                  '& .MuiInputLabel-root': { color: textSecondary },
                                }}
                              />
                              <TextField
                                label="Body Text"
                                placeholder="Describe your FAQ section..."
                                value={editor.body}
                                onChange={(e) => setEditor((prev) => ({ ...prev, body: e.target.value }))}
                                multiline
                                minRows={3}
                                size="small"
                                fullWidth
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: panelTint,
                                    borderRadius: 2,
                                    '& fieldset': { borderColor: 'rgba(148,163,184,0.15)' },
                                    '&:hover fieldset': { borderColor: alpha(blockTypeConfig.faq_categories.color, 0.4) },
                                    '&.Mui-focused fieldset': {
                                      borderColor: blockTypeConfig.faq_categories.color,
                                    },
                                  },
                                  '& .MuiInputBase-input': { color: textPrimary },
                                  '& .MuiInputLabel-root': { color: textSecondary },
                                }}
                              />
                              <TextField
                                label="Footer Text"
                                placeholder="Optional footer message"
                                value={editor.faqFooter}
                                onChange={(e) => setEditor((prev) => ({ ...prev, faqFooter: e.target.value }))}
                                size="small"
                                fullWidth
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    backgroundColor: panelTint,
                                    borderRadius: 2,
                                    '& fieldset': { borderColor: 'rgba(148,163,184,0.15)' },
                                    '&:hover fieldset': { borderColor: alpha(blockTypeConfig.faq_categories.color, 0.4) },
                                    '&.Mui-focused fieldset': {
                                      borderColor: blockTypeConfig.faq_categories.color,
                                    },
                                  },
                                  '& .MuiInputBase-input': { color: textPrimary },
                                  '& .MuiInputLabel-root': { color: textSecondary },
                                }}
                              />
                            </Stack>
                          </Box>

                          {/* Selection Section */}
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 2.5,
                              backgroundColor: alpha(blockTypeConfig.faq_categories.color, 0.04),
                              border: `1px solid ${alpha(blockTypeConfig.faq_categories.color, 0.1)}`,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: blockTypeConfig.faq_categories.color,
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                                mb: 1.5,
                                display: 'block',
                              }}
                            >
                              FAQ Selection
                            </Typography>
                            <Stack spacing={2}>
                              <FormControl size="small" fullWidth>
                                <InputLabel sx={{ color: textSecondary }}>Categories</InputLabel>
                                <Select
                                  label="Categories"
                                  multiple
                                  displayEmpty
                                  value={Array.isArray(editor.faqCategoryIds) ? editor.faqCategoryIds : []}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    const next = Array.isArray(value) ? value : String(value).split(',');
                                    const normalized = next
                                      .map((id) => Number(id))
                                      .filter((id) => Number.isFinite(id));
                                    setEditor((prev) => ({
                                      ...prev,
                                      faqCategoryIds: normalized,
                                    }));
                                  }}
                                  renderValue={(selected) => {
                                    const ids = Array.isArray(selected)
                                      ? selected.map((id) => Number(id)).filter((id) => Number.isFinite(id))
                                      : [];
                                    if (!ids.length) {
                                      return (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <Box
                                            sx={{
                                              px: 1.5,
                                              py: 0.25,
                                              borderRadius: 1,
                                              backgroundColor: alpha(blockTypeConfig.faq_categories.color, 0.15),
                                              color: blockTypeConfig.faq_categories.color,
                                              fontSize: '0.75rem',
                                              fontWeight: 500,
                                            }}
                                          >
                                            All Categories
                                          </Box>
                                        </Box>
                                      );
                                    }
                                    const names = faqCategories
                                      .filter((cat) => ids.includes(Number(cat.id)))
                                      .map((cat) => cat.name);
                                    return (
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {names.map((name, idx) => (
                                          <Box
                                            key={idx}
                                            sx={{
                                              px: 1,
                                              py: 0.25,
                                              borderRadius: 1,
                                              backgroundColor: alpha(blockTypeConfig.faq_categories.color, 0.15),
                                              color: blockTypeConfig.faq_categories.color,
                                              fontSize: '0.7rem',
                                              fontWeight: 500,
                                            }}
                                          >
                                            {name}
                                          </Box>
                                        ))}
                                      </Box>
                                    );
                                  }}
                                  sx={{
                                    backgroundColor: panelTint,
                                    borderRadius: 2,
                                    color: textPrimary,
                                    '& .MuiSvgIcon-root': { color: textSecondary },
                                    '& .MuiOutlinedInput-notchedOutline': {
                                      borderColor: 'rgba(148,163,184,0.15)',
                                    },
                                    '&:hover .MuiOutlinedInput-notchedOutline': {
                                      borderColor: alpha(blockTypeConfig.faq_categories.color, 0.4),
                                    },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                      borderColor: blockTypeConfig.faq_categories.color,
                                    },
                                  }}
                                >
                                  {faqCategories.map((category) => (
                                    <MenuItem key={category.id} value={category.id}>
                                      <Checkbox
                                        checked={
                                          Array.isArray(editor.faqCategoryIds) &&
                                          editor.faqCategoryIds.includes(category.id)
                                        }
                                        size="small"
                                        sx={{
                                          color: textSecondary,
                                          '&.Mui-checked': { color: blockTypeConfig.faq_categories.color },
                                          mr: 1,
                                          p: 0,
                                        }}
                                      />
                                      {category.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </FormControl>

                              <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                                  <Typography variant="caption" sx={{ color: textSecondary }}>
                                    FAQs
                                  </Typography>
                                  <Box
                                    sx={{
                                      px: 1,
                                      py: 0.25,
                                      borderRadius: 1,
                                      backgroundColor:
                                        Array.isArray(editor.faqIds) && editor.faqIds.length >= 10
                                          ? alpha('#ef4444', 0.15)
                                          : alpha(blockTypeConfig.faq_categories.color, 0.1),
                                      color:
                                        Array.isArray(editor.faqIds) && editor.faqIds.length >= 10
                                          ? '#ef4444'
                                          : textSecondary,
                                      fontSize: '0.7rem',
                                      fontWeight: 500,
                                    }}
                                  >
                                    {Array.isArray(editor.faqIds) ? editor.faqIds.length : 0} / 10 selected
                                  </Box>
                                </Box>
                                <FormControl size="small" fullWidth>
                                  <Select
                                    multiple
                                    displayEmpty
                                    value={Array.isArray(editor.faqIds) ? editor.faqIds : []}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      const next = Array.isArray(value) ? value : String(value).split(',');
                                      const normalized = next
                                        .map((id) => Number(id))
                                        .filter((id) => Number.isFinite(id));
                                      const capped = normalized.slice(0, 10);
                                      setEditor((prev) => ({
                                        ...prev,
                                        faqIds: capped,
                                      }));
                                    }}
                                    renderValue={(selected) => {
                                      const ids = Array.isArray(selected)
                                        ? selected.map((id) => Number(id)).filter((id) => Number.isFinite(id))
                                        : [];
                                      if (!ids.length) {
                                        return (
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Box
                                              sx={{
                                                px: 1.5,
                                                py: 0.25,
                                                borderRadius: 1,
                                                backgroundColor: alpha(blockTypeConfig.faq_categories.color, 0.15),
                                                color: blockTypeConfig.faq_categories.color,
                                                fontSize: '0.75rem',
                                                fontWeight: 500,
                                              }}
                                            >
                                              All FAQs
                                            </Box>
                                          </Box>
                                        );
                                      }
                                      const faqs = filteredFaqItems
                                        .filter((faq) => ids.includes(Number(faq.faq_id)));
                                      return (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                          {faqs.slice(0, 3).map((faq) => (
                                            <Box
                                              key={faq.faq_id}
                                              sx={{
                                                px: 1,
                                                py: 0.25,
                                                borderRadius: 1,
                                                backgroundColor: alpha(blockTypeConfig.faq_categories.color, 0.15),
                                                color: blockTypeConfig.faq_categories.color,
                                                fontSize: '0.7rem',
                                                fontWeight: 500,
                                                maxWidth: 120,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                              }}
                                            >
                                              {faq.faq_question}
                                            </Box>
                                          ))}
                                          {faqs.length > 3 && (
                                            <Box
                                              sx={{
                                                px: 1,
                                                py: 0.25,
                                                borderRadius: 1,
                                                backgroundColor: alpha(blockTypeConfig.faq_categories.color, 0.25),
                                                color: blockTypeConfig.faq_categories.color,
                                                fontSize: '0.7rem',
                                                fontWeight: 600,
                                              }}
                                            >
                                              +{faqs.length - 3} more
                                            </Box>
                                          )}
                                        </Box>
                                      );
                                    }}
                                    sx={{
                                      backgroundColor: panelTint,
                                      borderRadius: 2,
                                      color: textPrimary,
                                      '& .MuiSvgIcon-root': { color: textSecondary },
                                      '& .MuiOutlinedInput-notchedOutline': {
                                        borderColor: 'rgba(148,163,184,0.15)',
                                      },
                                      '&:hover .MuiOutlinedInput-notchedOutline': {
                                        borderColor: alpha(blockTypeConfig.faq_categories.color, 0.4),
                                      },
                                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: blockTypeConfig.faq_categories.color,
                                      },
                                    }}
                                  >
                                    {filteredFaqItems.map((faq) => {
                                      const isSelected = Array.isArray(editor.faqIds)
                                        ? editor.faqIds.includes(faq.faq_id)
                                        : false;
                                      const disableOption =
                                        !isSelected && Array.isArray(editor.faqIds) && editor.faqIds.length >= 10;
                                      return (
                                        <MenuItem
                                          key={faq.faq_id}
                                          value={faq.faq_id}
                                          disabled={disableOption}
                                          sx={{
                                            opacity: disableOption ? 0.5 : 1,
                                          }}
                                        >
                                          <Checkbox
                                            checked={isSelected}
                                            size="small"
                                            sx={{
                                              color: textSecondary,
                                              '&.Mui-checked': { color: blockTypeConfig.faq_categories.color },
                                              mr: 1,
                                              p: 0,
                                            }}
                                          />
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              whiteSpace: 'nowrap',
                                            }}
                                          >
                                            {faq.faq_question}
                                          </Typography>
                                        </MenuItem>
                                      );
                                    })}
                                  </Select>
                                  <FormHelperText sx={{ color: textSecondary, mt: 0.5 }}>
                                    Leave empty to include all FAQs from selected categories
                                  </FormHelperText>
                                </FormControl>
                              </Box>
                            </Stack>
                          </Box>

                          {/* Button Section */}
                          <Box
                            sx={{
                              p: 2,
                              borderRadius: 2.5,
                              backgroundColor: alpha(blockTypeConfig.faq_categories.color, 0.04),
                              border: `1px solid ${alpha(blockTypeConfig.faq_categories.color, 0.1)}`,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: blockTypeConfig.faq_categories.color,
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: 0.5,
                                mb: 1.5,
                                display: 'block',
                              }}
                            >
                              Button
                            </Typography>
                            <TextField
                              label="Button Label"
                              placeholder="e.g., View FAQs"
                              value={editor.faqButtonText}
                              onChange={(e) => setEditor((prev) => ({ ...prev, faqButtonText: e.target.value }))}
                              size="small"
                              fullWidth
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: panelTint,
                                  borderRadius: 2,
                                  '& fieldset': { borderColor: 'rgba(148,163,184,0.15)' },
                                  '&:hover fieldset': { borderColor: alpha(blockTypeConfig.faq_categories.color, 0.4) },
                                  '&.Mui-focused fieldset': {
                                    borderColor: blockTypeConfig.faq_categories.color,
                                  },
                                },
                                '& .MuiInputBase-input': { color: textPrimary },
                                '& .MuiInputLabel-root': { color: textSecondary },
                              }}
                            />
                          </Box>
                        </Stack>
                      </Box>
                    )}

                    {selectedBlock.type === 'contact' && (
                      <Box>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                          <Typography
                            variant="overline"
                            sx={{ color: textSecondary, fontWeight: 600, letterSpacing: 1, display: 'block' }}
                          >
                            Contacts
                          </Typography>
                          <Button
                            size="small"
                            startIcon={<Plus size={14} />}
                            onClick={handleAddContact}
                            sx={{
                              textTransform: 'none',
                              color: (blockTypeConfig[selectedBlock.type] || blockTypeConfig.text).color,
                            }}
                          >
                            Add Contact
                          </Button>
                        </Stack>
                        <Stack spacing={2}>
                          {editor.contacts.map((contact, idx) => (
                            <Box
                              key={`contact-${idx}`}
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                background: panelTint,
                                border: `1px solid ${cardBorder}`,
                              }}
                            >
                              <Stack spacing={1.5}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                  <Typography variant="subtitle2" sx={{ color: textPrimary }}>
                                    Contact {idx + 1}
                                  </Typography>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRemoveContact(idx)}
                                    sx={{
                                      color: '#ef4444',
                                      '&:hover': { background: 'rgba(239, 68, 68, 0.1)' },
                                    }}
                                  >
                                    <Trash2 size={16} />
                                  </IconButton>
                                </Stack>
                                <TextField
                                  label="Formatted Name"
                                  value={contact?.name?.formatted_name || ''}
                                  onChange={(e) => handleUpdateContactName(idx, 'formatted_name', e.target.value)}
                                  size="small"
                                  fullWidth
                                  sx={contactFieldSx}
                                />
                                <Stack direction="row" spacing={1}>
                                  <TextField
                                    label="First Name"
                                    value={contact?.name?.first_name || ''}
                                    onChange={(e) => handleUpdateContactName(idx, 'first_name', e.target.value)}
                                    size="small"
                                    fullWidth
                                    sx={contactFieldSx}
                                  />
                                  <TextField
                                    label="Last Name"
                                    value={contact?.name?.last_name || ''}
                                    onChange={(e) => handleUpdateContactName(idx, 'last_name', e.target.value)}
                                    size="small"
                                    fullWidth
                                    sx={contactFieldSx}
                                  />
                                </Stack>
                                <TextField
                                  label="Birthday (YYYY-MM-DD)"
                                  value={contact?.birthday || ''}
                                  onChange={(e) => handleUpdateContactField(idx, 'birthday', e.target.value)}
                                  size="small"
                                  fullWidth
                                  sx={contactFieldSx}
                                />

                                <Typography variant="caption" sx={{ color: textSecondary }}>
                                  Organization
                                </Typography>
                                <Stack direction="row" spacing={1}>
                                  <TextField
                                    label="Company"
                                    value={contact?.org?.company || ''}
                                    onChange={(e) => handleUpdateContactOrg(idx, 'company', e.target.value)}
                                    size="small"
                                    fullWidth
                                    sx={contactFieldSx}
                                  />
                                  <TextField
                                    label="Department"
                                    value={contact?.org?.department || ''}
                                    onChange={(e) => handleUpdateContactOrg(idx, 'department', e.target.value)}
                                    size="small"
                                    fullWidth
                                    sx={contactFieldSx}
                                  />
                                  <TextField
                                    label="Title"
                                    value={contact?.org?.title || ''}
                                    onChange={(e) => handleUpdateContactOrg(idx, 'title', e.target.value)}
                                    size="small"
                                    fullWidth
                                    sx={contactFieldSx}
                                  />
                                </Stack>

                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                  <Typography variant="caption" sx={{ color: textSecondary }}>
                                    Phones
                                  </Typography>
                                  <Button
                                    size="small"
                                    onClick={() =>
                                      handleAddContactListItem(idx, 'phones', {
                                        phone: '',
                                      })
                                    }
                                    disabled={(contact?.phones || []).length >= 1}
                                    sx={{ textTransform: 'none' }}
                                  >
                                    Add Phone
                                  </Button>
                                </Stack>
                                {(contact?.phones || []).map((phone, phoneIdx) => (
                                  <Stack key={`phone-${phoneIdx}`} direction="row" spacing={1}>
                                    <TextField
                                      label="Phone"
                                      value={phone?.phone || ''}
                                      onChange={(e) =>
                                        handleUpdateContactListItem(idx, 'phones', phoneIdx, 'phone', e.target.value)
                                      }
                                      size="small"
                                      fullWidth
                                      sx={contactFieldSx}
                                    />
                                    <IconButton
                                      size="small"
                                      onClick={() => handleRemoveContactListItem(idx, 'phones', phoneIdx)}
                                      sx={{ color: '#ef4444' }}
                                    >
                                      <Trash2 size={16} />
                                    </IconButton>
                                  </Stack>
                                ))}

                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                  <Typography variant="caption" sx={{ color: textSecondary }}>
                                    Emails
                                  </Typography>
                                  <Button
                                    size="small"
                                    onClick={() =>
                                      handleAddContactListItem(idx, 'emails', { email: '' })
                                    }
                                    disabled={(contact?.emails || []).length >= 1}
                                    sx={{ textTransform: 'none' }}
                                  >
                                    Add Email
                                  </Button>
                                </Stack>
                                {(contact?.emails || []).map((email, emailIdx) => (
                                  <Stack key={`email-${emailIdx}`} direction="row" spacing={1}>
                                    <TextField
                                      label="Email"
                                      value={email?.email || ''}
                                      onChange={(e) =>
                                        handleUpdateContactListItem(idx, 'emails', emailIdx, 'email', e.target.value)
                                      }
                                      size="small"
                                      fullWidth
                                      sx={contactFieldSx}
                                    />
                                    <IconButton
                                      size="small"
                                      onClick={() => handleRemoveContactListItem(idx, 'emails', emailIdx)}
                                      sx={{ color: '#ef4444' }}
                                    >
                                      <Trash2 size={16} />
                                    </IconButton>
                                  </Stack>
                                ))}

                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                  <Typography variant="caption" sx={{ color: textSecondary }}>
                                    Addresses
                                  </Typography>
                                  <Button
                                    size="small"
                                    onClick={() =>
                                      handleAddContactListItem(idx, 'addresses', {
                                        street: '',
                                        city: '',
                                        state: '',
                                        zip: '',
                                        country: '',
                                        country_code: '',
                                      })
                                    }
                                    disabled={(contact?.addresses || []).length >= 1}
                                    sx={{ textTransform: 'none' }}
                                  >
                                    Add Address
                                  </Button>
                                </Stack>
                                {(contact?.addresses || []).map((address, addressIdx) => (
                                  <Stack key={`address-${addressIdx}`} spacing={1}>
                                    <Stack direction="row" spacing={1}>
                                      <TextField
                                        label="Street"
                                        value={address?.street || ''}
                                        onChange={(e) =>
                                          handleUpdateContactListItem(
                                            idx,
                                            'addresses',
                                            addressIdx,
                                            'street',
                                            e.target.value
                                          )
                                        }
                                        size="small"
                                        fullWidth
                                        sx={contactFieldSx}
                                      />
                                      <TextField
                                        label="City"
                                        value={address?.city || ''}
                                        onChange={(e) =>
                                          handleUpdateContactListItem(
                                            idx,
                                            'addresses',
                                            addressIdx,
                                            'city',
                                            e.target.value
                                          )
                                        }
                                        size="small"
                                        fullWidth
                                        sx={contactFieldSx}
                                      />
                                    </Stack>
                                    <Stack direction="row" spacing={1}>
                                      <TextField
                                        label="State"
                                        value={address?.state || ''}
                                        onChange={(e) =>
                                          handleUpdateContactListItem(
                                            idx,
                                            'addresses',
                                            addressIdx,
                                            'state',
                                            e.target.value
                                          )
                                        }
                                        size="small"
                                        fullWidth
                                        sx={contactFieldSx}
                                      />
                                      <TextField
                                        label="ZIP"
                                        value={address?.zip || ''}
                                        onChange={(e) =>
                                          handleUpdateContactListItem(
                                            idx,
                                            'addresses',
                                            addressIdx,
                                            'zip',
                                            e.target.value
                                          )
                                        }
                                        size="small"
                                        fullWidth
                                        sx={contactFieldSx}
                                      />
                                    </Stack>
                                    <Stack direction="row" spacing={1}>
                                      <TextField
                                        label="Country"
                                        value={address?.country || ''}
                                        onChange={(e) =>
                                          handleUpdateContactListItem(
                                            idx,
                                            'addresses',
                                            addressIdx,
                                            'country',
                                            e.target.value
                                          )
                                        }
                                        size="small"
                                        fullWidth
                                        sx={contactFieldSx}
                                      />
                                      <TextField
                                        label="Country Code"
                                        value={address?.country_code || ''}
                                        onChange={(e) =>
                                          handleUpdateContactListItem(
                                            idx,
                                            'addresses',
                                            addressIdx,
                                            'country_code',
                                            e.target.value
                                          )
                                        }
                                        size="small"
                                        fullWidth
                                        sx={contactFieldSx}
                                      />
                                    </Stack>
                                    <Stack direction="row" justifyContent="flex-end">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleRemoveContactListItem(idx, 'addresses', addressIdx)}
                                        sx={{ color: '#ef4444' }}
                                      >
                                        <Trash2 size={16} />
                                      </IconButton>
                                    </Stack>
                                  </Stack>
                                ))}

                                <Stack direction="row" justifyContent="space-between" alignItems="center">
                                  <Typography variant="caption" sx={{ color: textSecondary }}>
                                    URLs
                                  </Typography>
                                  <Button
                                    size="small"
                                    onClick={() =>
                                      handleAddContactListItem(idx, 'urls', { url: '' })
                                    }
                                    sx={{ textTransform: 'none' }}
                                  >
                                    Add URL
                                  </Button>
                                </Stack>
                                {(contact?.urls || []).map((url, urlIdx) => (
                                  <Stack key={`url-${urlIdx}`} direction="row" spacing={1}>
                                    <TextField
                                      label="URL"
                                      value={url?.url || ''}
                                      onChange={(e) =>
                                        handleUpdateContactListItem(idx, 'urls', urlIdx, 'url', e.target.value)
                                      }
                                      size="small"
                                      fullWidth
                                      sx={contactFieldSx}
                                    />
                                    <IconButton
                                      size="small"
                                      onClick={() => handleRemoveContactListItem(idx, 'urls', urlIdx)}
                                      sx={{ color: '#ef4444' }}
                                    >
                                      <Trash2 size={16} />
                                    </IconButton>
                                  </Stack>
                                ))}
                              </Stack>
                            </Box>
                          ))}
                        </Stack>
                        {editor.contacts.length === 0 && (
                          <Typography variant="caption" sx={{ color: textSecondary, display: 'block', mt: 1 }}>
                            Add at least one contact with a formatted name.
                          </Typography>
                        )}
                      </Box>
                    )}

                    {/* Button Content */}
                    {selectedBlock.type === 'interactive_button' && (
                      <Box>
                        <Typography
                          variant="overline"
                          sx={{ color: textSecondary, fontWeight: 600, letterSpacing: 1, mb: 1, display: 'block' }}
                        >
                          Button Content
                        </Typography>
                        <Stack spacing={1.5}>
                          <Stack direction="row" spacing={1}>
                            <FormControl size="small" sx={{ minWidth: 140 }}>
                              <InputLabel sx={{ color: textSecondary }}>Header Type</InputLabel>
                              <Select
                                label="Header Type"
                                value={editor.buttonHeaderType}
                                onChange={(e) =>
                                  setEditor((prev) => ({ ...prev, buttonHeaderType: e.target.value }))
                                }
                                sx={{
                                  backgroundColor: panelTint,
                                  borderRadius: 2,
                                  color: textPrimary,
                                  '& .MuiSvgIcon-root': { color: textSecondary },
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(148,163,184,0.15)',
                                  },
                                }}
                              >
                                <MenuItem value="text">Text</MenuItem>
                                <MenuItem value="image">Image</MenuItem>
                                <MenuItem value="video">Video</MenuItem>
                                <MenuItem value="document">Document</MenuItem>
                              </Select>
                            </FormControl>
                            <TextField
                              label={editor.buttonHeaderType === 'text' ? 'Header Text' : 'Header URL'}
                              value={
                                editor.buttonHeaderType === 'text'
                                  ? editor.buttonHeader
                                  : editor.buttonHeaderLink
                              }
                              onChange={(e) =>
                                setEditor((prev) =>
                                  editor.buttonHeaderType === 'text'
                                    ? { ...prev, buttonHeader: e.target.value }
                                    : { ...prev, buttonHeaderLink: e.target.value }
                                )
                              }
                              size="small"
                              fullWidth
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: panelTint,
                                  borderRadius: 2,
                                  '& fieldset': { borderColor: 'rgba(148,163,184,0.15)' },
                                  '&:hover fieldset': { borderColor: 'rgba(148,163,184,0.3)' },
                                  '&.Mui-focused fieldset': {
                                    borderColor: (blockTypeConfig[selectedBlock.type] || blockTypeConfig.text).color,
                                  },
                                },
                                '& .MuiInputBase-input': { color: textPrimary },
                                '& .MuiInputLabel-root': { color: textSecondary },
                              }}
                            />
                          </Stack>
                          {editor.buttonHeaderType !== 'text' && (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => buttonHeaderInputRef.current?.click()}
                                disabled={buttonHeaderUploading}
                                sx={{
                                  textTransform: 'none',
                                  color: (blockTypeConfig[selectedBlock.type] || blockTypeConfig.text).color,
                                  borderColor: alpha(
                                    (blockTypeConfig[selectedBlock.type] || blockTypeConfig.text).color,
                                    0.5
                                  ),
                                }}
                              >
                                {buttonHeaderUploading ? 'Uploading...' : 'Upload Media'}
                              </Button>
                              <Typography variant="caption" sx={{ color: textSecondary }}>
                                {String(editor.buttonHeaderType).toUpperCase()} only
                              </Typography>
                              <input
                                ref={buttonHeaderInputRef}
                                type="file"
                                accept={
                                  FILE_ACCEPT_STRINGS[editor.buttonHeaderType] || FILE_ACCEPT_STRINGS.all
                                }
                                style={{ display: 'none' }}
                                onChange={handleButtonHeaderUpload}
                              />
                            </Stack>
                          )}
                          <TextField
                            label="Body Text"
                            value={editor.body}
                            onChange={(e) => setEditor((prev) => ({ ...prev, body: e.target.value }))}
                            multiline
                            minRows={3}
                            size="small"
                            fullWidth
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: panelTint,
                                borderRadius: 2,
                                '& fieldset': { borderColor: 'rgba(148,163,184,0.15)' },
                                '&:hover fieldset': { borderColor: 'rgba(148,163,184,0.3)' },
                                '&.Mui-focused fieldset': {
                                  borderColor: (blockTypeConfig[selectedBlock.type] || blockTypeConfig.text).color,
                                },
                              },
                              '& .MuiInputBase-input': { color: textPrimary },
                              '& .MuiInputLabel-root': { color: textSecondary },
                            }}
                          />
                          <TextField
                            label="Footer Text"
                            value={editor.buttonFooter}
                            onChange={(e) => setEditor((prev) => ({ ...prev, buttonFooter: e.target.value }))}
                            size="small"
                            fullWidth
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: panelTint,
                                borderRadius: 2,
                                '& fieldset': { borderColor: 'rgba(148,163,184,0.15)' },
                                '&:hover fieldset': { borderColor: 'rgba(148,163,184,0.3)' },
                                '&.Mui-focused fieldset': {
                                  borderColor: (blockTypeConfig[selectedBlock.type] || blockTypeConfig.text).color,
                                },
                              },
                              '& .MuiInputBase-input': { color: textPrimary },
                              '& .MuiInputLabel-root': { color: textSecondary },
                            }}
                          />
                        </Stack>
                      </Box>
                    )}

                    {/* List Content */}
                    {selectedBlock.type === 'interactive_list' && (
                      <Box>
                        <Typography
                          variant="overline"
                          sx={{ color: textSecondary, fontWeight: 600, letterSpacing: 1, mb: 1, display: 'block' }}
                        >
                          List Content
                        </Typography>
                        <Stack spacing={1.5}>
                          <TextField
                            label="Header Text"
                            value={editor.listHeader}
                            onChange={(e) => setEditor((prev) => ({ ...prev, listHeader: e.target.value }))}
                            size="small"
                            fullWidth
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: panelTint,
                                borderRadius: 2,
                                '& fieldset': { borderColor: 'rgba(148,163,184,0.15)' },
                                '&:hover fieldset': { borderColor: 'rgba(148,163,184,0.3)' },
                                '&.Mui-focused fieldset': {
                                  borderColor: (blockTypeConfig[selectedBlock.type] || blockTypeConfig.text).color,
                                },
                              },
                              '& .MuiInputBase-input': { color: textPrimary },
                              '& .MuiInputLabel-root': { color: textSecondary },
                            }}
                          />
                          <TextField
                            label="Body Text"
                            value={editor.body}
                            onChange={(e) => setEditor((prev) => ({ ...prev, body: e.target.value }))}
                            multiline
                            minRows={3}
                            size="small"
                            fullWidth
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: panelTint,
                                borderRadius: 2,
                                '& fieldset': { borderColor: 'rgba(148,163,184,0.15)' },
                                '&:hover fieldset': { borderColor: 'rgba(148,163,184,0.3)' },
                                '&.Mui-focused fieldset': {
                                  borderColor: (blockTypeConfig[selectedBlock.type] || blockTypeConfig.text).color,
                                },
                              },
                              '& .MuiInputBase-input': { color: textPrimary },
                              '& .MuiInputLabel-root': { color: textSecondary },
                            }}
                          />
                          <TextField
                            label="Footer Text"
                            value={editor.listFooter}
                            onChange={(e) => setEditor((prev) => ({ ...prev, listFooter: e.target.value }))}
                            size="small"
                            fullWidth
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: panelTint,
                                borderRadius: 2,
                                '& fieldset': { borderColor: 'rgba(148,163,184,0.15)' },
                                '&:hover fieldset': { borderColor: 'rgba(148,163,184,0.3)' },
                                '&.Mui-focused fieldset': {
                                  borderColor: (blockTypeConfig[selectedBlock.type] || blockTypeConfig.text).color,
                                },
                              },
                              '& .MuiInputBase-input': { color: textPrimary },
                              '& .MuiInputLabel-root': { color: textSecondary },
                            }}
                          />
                          <TextField
                            label="Button Text"
                            value={editor.listButtonText}
                            onChange={(e) =>
                              setEditor((prev) => ({ ...prev, listButtonText: e.target.value }))
                            }
                            size="small"
                            fullWidth
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: panelTint,
                                borderRadius: 2,
                                '& fieldset': { borderColor: 'rgba(148,163,184,0.15)' },
                                '&:hover fieldset': { borderColor: 'rgba(148,163,184,0.3)' },
                                '&.Mui-focused fieldset': {
                                  borderColor: (blockTypeConfig[selectedBlock.type] || blockTypeConfig.text).color,
                                },
                              },
                              '& .MuiInputBase-input': { color: textPrimary },
                              '& .MuiInputLabel-root': { color: textSecondary },
                            }}
                          />
                        </Stack>
                      </Box>
                    )}

                    {/* CTA URL Content */}
                    {selectedBlock.type === 'cta_url' && (
                      <Box>
                        <Typography
                          variant="overline"
                          sx={{ color: textSecondary, fontWeight: 600, letterSpacing: 1, mb: 1, display: 'block' }}
                        >
                          CTA URL Content
                        </Typography>
                        <Stack spacing={1.5}>
                          <Stack direction="row" spacing={1}>
                            <FormControl size="small" sx={{ minWidth: 140 }}>
                              <InputLabel sx={{ color: textSecondary }}>Header Type</InputLabel>
                              <Select
                                label="Header Type"
                                value={editor.ctaHeaderType}
                                onChange={(e) =>
                                  setEditor((prev) => ({ ...prev, ctaHeaderType: e.target.value }))
                                }
                                sx={{
                                  backgroundColor: panelTint,
                                  borderRadius: 2,
                                  color: textPrimary,
                                  '& .MuiSvgIcon-root': { color: textSecondary },
                                  '& .MuiOutlinedInput-notchedOutline': {
                                    borderColor: 'rgba(148,163,184,0.15)',
                                  },
                                }}
                              >
                                <MenuItem value="text">Text</MenuItem>
                                <MenuItem value="image">Image</MenuItem>
                                <MenuItem value="video">Video</MenuItem>
                                <MenuItem value="document">Document</MenuItem>
                              </Select>
                            </FormControl>
                            <TextField
                              label={editor.ctaHeaderType === 'text' ? 'Header Text' : 'Header URL'}
                              value={
                                editor.ctaHeaderType === 'text' ? editor.ctaHeader : editor.ctaHeaderLink
                              }
                              onChange={(e) =>
                                setEditor((prev) =>
                                  editor.ctaHeaderType === 'text'
                                    ? { ...prev, ctaHeader: e.target.value }
                                    : { ...prev, ctaHeaderLink: e.target.value }
                                )
                              }
                              size="small"
                              fullWidth
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: panelTint,
                                  borderRadius: 2,
                                  '& fieldset': { borderColor: 'rgba(148,163,184,0.15)' },
                                  '&:hover fieldset': { borderColor: 'rgba(148,163,184,0.3)' },
                                  '&.Mui-focused fieldset': {
                                    borderColor: (blockTypeConfig[selectedBlock.type] || blockTypeConfig.text).color,
                                  },
                                },
                                '& .MuiInputBase-input': { color: textPrimary },
                                '& .MuiInputLabel-root': { color: textSecondary },
                              }}
                            />
                          </Stack>
                          {editor.ctaHeaderType !== 'text' && (
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => ctaHeaderInputRef.current?.click()}
                                disabled={ctaHeaderUploading}
                                sx={{
                                  textTransform: 'none',
                                  color: (blockTypeConfig[selectedBlock.type] || blockTypeConfig.text).color,
                                  borderColor: alpha(
                                    (blockTypeConfig[selectedBlock.type] || blockTypeConfig.text).color,
                                    0.5
                                  ),
                                }}
                              >
                                {ctaHeaderUploading ? 'Uploading...' : 'Upload Media'}
                              </Button>
                              <Typography variant="caption" sx={{ color: textSecondary }}>
                                {String(editor.ctaHeaderType).toUpperCase()} only
                              </Typography>
                              <input
                                ref={ctaHeaderInputRef}
                                type="file"
                                accept={
                                  FILE_ACCEPT_STRINGS[editor.ctaHeaderType] || FILE_ACCEPT_STRINGS.all
                                }
                                style={{ display: 'none' }}
                                onChange={handleCtaHeaderUpload}
                              />
                            </Stack>
                          )}
                          <TextField
                            label="Body Text"
                            value={editor.body}
                            onChange={(e) => setEditor((prev) => ({ ...prev, body: e.target.value }))}
                            multiline
                            minRows={3}
                            size="small"
                            fullWidth
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: panelTint,
                                borderRadius: 2,
                                '& fieldset': { borderColor: 'rgba(148,163,184,0.15)' },
                                '&:hover fieldset': { borderColor: 'rgba(148,163,184,0.3)' },
                                '&.Mui-focused fieldset': {
                                  borderColor: (blockTypeConfig[selectedBlock.type] || blockTypeConfig.text).color,
                                },
                              },
                              '& .MuiInputBase-input': { color: textPrimary },
                              '& .MuiInputLabel-root': { color: textSecondary },
                            }}
                          />
                          <TextField
                            label="Footer Text"
                            value={editor.ctaFooter}
                            onChange={(e) => setEditor((prev) => ({ ...prev, ctaFooter: e.target.value }))}
                            size="small"
                            fullWidth
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: panelTint,
                                borderRadius: 2,
                                '& fieldset': { borderColor: 'rgba(148,163,184,0.15)' },
                                '&:hover fieldset': { borderColor: 'rgba(148,163,184,0.3)' },
                                '&.Mui-focused fieldset': {
                                  borderColor: (blockTypeConfig[selectedBlock.type] || blockTypeConfig.text).color,
                                },
                              },
                              '& .MuiInputBase-input': { color: textPrimary },
                              '& .MuiInputLabel-root': { color: textSecondary },
                            }}
                          />
                          <TextField
                            label="Button Label"
                            value={editor.ctaButtonText}
                            onChange={(e) => setEditor((prev) => ({ ...prev, ctaButtonText: e.target.value }))}
                            size="small"
                            fullWidth
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: panelTint,
                                borderRadius: 2,
                                '& fieldset': { borderColor: 'rgba(148,163,184,0.15)' },
                                '&:hover fieldset': { borderColor: 'rgba(148,163,184,0.3)' },
                                '&.Mui-focused fieldset': {
                                  borderColor: (blockTypeConfig[selectedBlock.type] || blockTypeConfig.text).color,
                                },
                              },
                              '& .MuiInputBase-input': { color: textPrimary },
                              '& .MuiInputLabel-root': { color: textSecondary },
                            }}
                          />
                          <TextField
                            label="Button URL"
                            value={editor.url}
                            onChange={(e) => setEditor((prev) => ({ ...prev, url: e.target.value }))}
                            size="small"
                            fullWidth
                            placeholder="https://example.com"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: panelTint,
                                borderRadius: 2,
                                '& fieldset': { borderColor: 'rgba(148,163,184,0.15)' },
                                '&:hover fieldset': { borderColor: 'rgba(148,163,184,0.3)' },
                                '&.Mui-focused fieldset': {
                                  borderColor: (blockTypeConfig[selectedBlock.type] || blockTypeConfig.text).color,
                                },
                              },
                              '& .MuiInputBase-input': { color: textPrimary },
                              '& .MuiInputLabel-root': { color: textSecondary },
                            }}
                          />
                        </Stack>
                      </Box>
                    )}

                    {/* Interactive Options */}
                    {['interactive_button', 'interactive_list'].includes(selectedBlock.type) && (
                      <Box>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
                          <Typography
                            variant="overline"
                            sx={{ color: textSecondary, fontWeight: 600, letterSpacing: 1 }}
                          >
                            Options ({editor.items.length})
                          </Typography>
                          <Button
                            size="small"
                            startIcon={<Plus size={14} />}
                            onClick={handleAddItem}
                            sx={{
                              textTransform: 'none',
                              color: (blockTypeConfig[selectedBlock.type] || blockTypeConfig.text).color,
                            }}
                            disabled={
                              (selectedBlock.type === 'interactive_button' && editor.items.length >= 3) ||
                              (selectedBlock.type === 'interactive_list' && editor.items.length >= 10)
                            }
                          >
                            Add
                          </Button>
                        </Stack>
                        <Stack spacing={1.5}>
                          {editor.items.map((item, idx) => (
                            <Box
                              key={`${item.id}-${idx}`}
                              sx={{
                                p: 1.5,
                                borderRadius: 2,
                                background: panelTint,
                                border: `1px solid ${cardBorder}`,
                              }}
                            >
                              <Stack spacing={1}>
                                <TextField
                                  value={item.title}
                                  onChange={(e) => handleUpdateItem(idx, 'title', e.target.value)}
                                  size="small"
                                  placeholder="Button title"
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      backgroundColor: panelTint,
                                      borderRadius: 1.5,
                                      '& fieldset': { borderColor: cardBorder },
                                      '&:hover fieldset': { borderColor: alpha(textPrimary, 0.3) },
                                    },
                                    '& .MuiInputBase-input': { color: textPrimary, fontSize: '0.875rem' },
                                  }}
                                />
                                <Stack direction="row" spacing={1}>
                                  <TextField
                                    value={item.id}
                                    size="small"
                                    placeholder="ID"
                                    disabled
                                    sx={{
                                      flex: 1,
                                      '& .MuiOutlinedInput-root': {
                                        backgroundColor: panelTint,
                                        borderRadius: 1.5,
                                        '& fieldset': { borderColor: cardBorder },
                                        '&:hover fieldset': { borderColor: alpha(textPrimary, 0.3) },
                                      },
                                      '& .MuiInputBase-input': { color: textSecondary, fontSize: '0.75rem' },
                                    }}
                                  />
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRemoveItem(idx)}
                                    sx={{
                                      color: '#ef4444',
                                      '&:hover': { background: 'rgba(239, 68, 68, 0.1)' },
                                    }}
                                  >
                                    <Trash2 size={16} />
                                  </IconButton>
                                </Stack>
                              </Stack>
                            </Box>
                          ))}
                        </Stack>
                      </Box>
                    )}

                    {/* Location Details */}
                    {selectedBlock.type === 'location' && (
                      <Box>
                        <Typography
                          variant="overline"
                          sx={{ color: textSecondary, fontWeight: 600, letterSpacing: 1, mb: 1, display: 'block' }}
                        >
                          Location Details
                        </Typography>
                        <Stack spacing={1.5}>
                          <Stack direction="row" spacing={1}>
                            <TextField
                              label="Latitude"
                              value={editor.location.latitude}
                              onChange={(e) =>
                                setEditor((prev) => ({
                                  ...prev,
                                  location: { ...prev.location, latitude: e.target.value },
                                }))
                              }
                              size="small"
                              sx={{
                                flex: 1,
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: panelTint,
                                  borderRadius: 2,
                                  '& fieldset': { borderColor: 'rgba(148,163,184,0.15)' },
                                },
                                '& .MuiInputBase-input': { color: textPrimary },
                                '& .MuiInputLabel-root': { color: textSecondary },
                              }}
                            />
                            <TextField
                              label="Longitude"
                              value={editor.location.longitude}
                              onChange={(e) =>
                                setEditor((prev) => ({
                                  ...prev,
                                  location: { ...prev.location, longitude: e.target.value },
                                }))
                              }
                              size="small"
                              sx={{
                                flex: 1,
                                '& .MuiOutlinedInput-root': {
                                  backgroundColor: panelTint,
                                  borderRadius: 2,
                                  '& fieldset': { borderColor: 'rgba(148,163,184,0.15)' },
                                },
                                '& .MuiInputBase-input': { color: textPrimary },
                                '& .MuiInputLabel-root': { color: textSecondary },
                              }}
                            />
                          </Stack>
                          <TextField
                            label="Place Name"
                            value={editor.location.name}
                            onChange={(e) =>
                              setEditor((prev) => ({
                                ...prev,
                                location: { ...prev.location, name: e.target.value },
                              }))
                            }
                            size="small"
                            fullWidth
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: panelTint,
                                borderRadius: 2,
                                '& fieldset': { borderColor: 'rgba(148,163,184,0.15)' },
                              },
                              '& .MuiInputBase-input': { color: textPrimary },
                              '& .MuiInputLabel-root': { color: textSecondary },
                            }}
                          />
                          <TextField
                            label="Address"
                            value={editor.location.address}
                            onChange={(e) =>
                              setEditor((prev) => ({
                                ...prev,
                                location: { ...prev.location, address: e.target.value },
                              }))
                            }
                            size="small"
                            fullWidth
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: panelTint,
                                borderRadius: 2,
                                '& fieldset': { borderColor: 'rgba(148,163,184,0.15)' },
                              },
                              '& .MuiInputBase-input': { color: textPrimary },
                              '& .MuiInputLabel-root': { color: textSecondary },
                            }}
                          />
                        </Stack>
                      </Box>
                    )}

                    {/* Template Details */}
                    {selectedBlock.type === 'template' && (
                      <Box>
                        <Typography
                          variant="overline"
                          sx={{ color: textSecondary, fontWeight: 600, letterSpacing: 1, mb: 1, display: 'block' }}
                        >
                          Template Details
                        </Typography>
                        <Stack spacing={1.5}>
                          <TextField
                            label="Template Name"
                            value={editor.template.name}
                            onChange={(e) =>
                              setEditor((prev) => ({
                                ...prev,
                                template: { ...prev.template, name: e.target.value },
                              }))
                            }
                            size="small"
                            fullWidth
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: panelTint,
                                borderRadius: 2,
                                '& fieldset': { borderColor: 'rgba(148,163,184,0.15)' },
                              },
                              '& .MuiInputBase-input': { color: textPrimary },
                              '& .MuiInputLabel-root': { color: textSecondary },
                            }}
                          />
                          <TextField
                            label="Language Code"
                            value={editor.template.language}
                            onChange={(e) =>
                              setEditor((prev) => ({
                                ...prev,
                                template: { ...prev.template, language: e.target.value },
                              }))
                            }
                            size="small"
                            fullWidth
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                backgroundColor: panelTint,
                                borderRadius: 2,
                                '& fieldset': { borderColor: 'rgba(148,163,184,0.15)' },
                              },
                              '& .MuiInputBase-input': { color: textPrimary },
                              '& .MuiInputLabel-root': { color: textSecondary },
                            }}
                          />
                        </Stack>
                      </Box>
                    )}

                    <Divider sx={{ borderColor: 'rgba(148,163,184,0.08)' }} />

                    {/* Connect To Section */}
                    {!['interactive_button', 'interactive_list', 'carousel', 'faq_categories', 'live_chat'].includes(selectedBlock.type) && (
                      <Box>
                        <Typography
                          variant="overline"
                          sx={{ color: textSecondary, fontWeight: 600, letterSpacing: 1, mb: 1, display: 'block' }}
                        >
                          Connections
                        </Typography>
                        <Stack spacing={1.5}>
                          <FormControl size="small" fullWidth>
                            <InputLabel sx={{ color: textSecondary }}>Target Block</InputLabel>
                            <Select
                              label="Target Block"
                              value={editor.connectTo}
                              onChange={(e) => setEditor((prev) => ({ ...prev, connectTo: e.target.value }))}
                              sx={{
                                backgroundColor: panelTint,
                                borderRadius: 2,
                                color: textPrimary,
                                '& .MuiSvgIcon-root': { color: textSecondary },
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(148,163,184,0.15)' },
                              }}
                            >
                              {blocks
                                .filter((block) => block.id !== selectedBlock.id)
                                .map((block) => (
                                  <MenuItem key={block.id} value={block.id}>
                                    {block.name}
                                  </MenuItem>
                                ))}
                            </Select>
                          </FormControl>

                          <Button
                            variant="contained"
                            onClick={handleConnectFromPanel}
                            startIcon={<Cable size={16} />}
                            sx={{
                              background: 'linear-gradient(135deg, #38bdf8 0%, #0ea5e9 100%)',
                              color: '#fff',
                              borderRadius: 2,
                              textTransform: 'none',
                              fontWeight: 600,
                            }}
                          >
                            Create Connection
                          </Button>
                        </Stack>
                      </Box>
                    )}

                    {/* Existing Connections */}
                    {selectedBlock.connections?.length > 0 && (
                      <Box>
                        <Typography
                          variant="overline"
                          sx={{ color: textSecondary, fontWeight: 600, letterSpacing: 1, mb: 1, display: 'block' }}
                        >
                          Active Connections ({selectedBlock.connections.length})
                        </Typography>
                        <Stack spacing={1}>
                          {selectedBlock.connections.map((conn) => {
                            const targetBlock = blocks.find((b) => b.id === conn.to_block_id);
                            const targetConfig = targetBlock
                              ? blockTypeConfig[targetBlock.type] || blockTypeConfig.text
                              : blockTypeConfig.text;
                            return (
                              <Stack
                                key={conn.connection_id}
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{
                                  p: 1.5,
                                  borderRadius: 2,
                                  background: panelTint,
                                  border: `1px solid ${cardBorder}`,
                                }}
                              >
                                <Stack direction="row" spacing={1} alignItems="center">
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: '50%',
                                      background: targetConfig.color,
                                    }}
                                  />
                                  <Typography variant="caption" sx={{ color: textSecondary }}>
                                    {conn.trigger !== 'auto' ? `${conn.trigger} ->` : 'Auto ->'}{' '}
                                    <span style={{ color: textPrimary }}>
                                      {targetBlock?.name || 'Unknown'}
                                    </span>
                                  </Typography>
                                </Stack>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteConnection(conn.connection_id)}
                                  sx={{
                                    color: '#ef4444',
                                    '&:hover': { background: 'rgba(239, 68, 68, 0.1)' },
                                  }}
                                >
                                  <Trash2 size={14} />
                                </IconButton>
                              </Stack>
                            );
                          })}
                        </Stack>
                      </Box>
                    )}
                  </Stack>
                </Box>

                {/* Panel Footer - Actions */}
                <Box
                  sx={{
                    px: 2.5,
                    py: 2,
                    borderTop: `1px solid ${borderTone}`,
                    background: panelTint,
                  }}
                >
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      startIcon={<Save size={16} />}
                      onClick={handleSaveBlock}
                      fullWidth
                      sx={{
                        background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                        color: '#fff',
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        py: 1,
                      }}
                    >
                      Save Block
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<Trash2 size={16} />}
                      onClick={handleDeleteBlock}
                      sx={{
                        borderColor: 'rgba(239, 68, 68, 0.5)',
                        color: '#ef4444',
                        borderRadius: 2,
                        textTransform: 'none',
                        '&:hover': {
                          borderColor: '#ef4444',
                          background: 'rgba(239, 68, 68, 0.1)',
                        },
                      }}
                    >
                      Delete
                    </Button>
                  </Stack>
                </Box>
              </>
            )}
          </Box>
          )}
        </Stack>
      )}
    </Box>
  );
};

export default BotBuilderPage;
